// src/components/portal/TablesView.js
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { collection, doc, onSnapshot, getDocs, updateDoc, deleteDoc, addDoc, deleteField, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Portal from '../../Portal';
import { formatPhone } from '../../utilities/formatPhone';
import { getTableAvailabilityGroups } from '../../utilities/tableAvailability';

const _updateWaitlistEta  = httpsCallable(getFunctions(), 'updateWaitlistEta');
const _declinePickupOrder = httpsCallable(getFunctions(), 'declinePickupOrder');

// Reservation statuses that mean the table is free again (party gone / cancelled).
// These must be excluded from conflict checks, time-table rendering, and upcoming panels.
// fullyPaid  — CF confirmed all items paid (online reservations; table released immediately)
// completed  — staff confirmed table cleaned via the bussing flow
// paid/bussing are intentionally NOT included — they represent lingering/clearing states
//              that should still show on the portal floor map.
const CLOSED_STATUSES = new Set(['comped', 'voided', 'walk_out', 'cancelled', 'canceled', 'fullyPaid', 'completed', 'noShow']);

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function fmt12(timeStr) {
  if (!timeStr) return '';
  if (/[AP]M/i.test(timeStr)) {
    const m = timeStr.match(/^(\d+):(\d+)\s*([AP]M)/i);
    return m ? `${m[1]}:${m[2]} ${m[3].toUpperCase()}` : timeStr;
  }
  const [hStr, mStr] = timeStr.split(':');
  let h = parseInt(hStr, 10);
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${mStr} ${suffix}`;
}

function getInitials(name) {
  if (!name) return null;
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ─── Block helpers ─────────────────────────────────────────────────────────────

function isBlockActive(block, nowMs = Date.now()) {
  if (!block) return false;
  const start = block.startsAt ? new Date(block.startsAt).getTime() : null;
  const end   = block.expiresAt ? new Date(block.expiresAt).getTime() : null;
  if (start && start > nowMs) return false;
  if (end && end <= nowMs) return false;
  return true;
}

function isBlockScheduled(block, nowMs = Date.now()) {
  if (!block) return false;
  return block.startsAt && new Date(block.startsAt).getTime() > nowMs;
}

function formatBlockExpiry(block) {
  if (!block) return '';
  const fmt = (iso) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  if (isBlockScheduled(block)) {
    const start = fmt(block.startsAt);
    const end = block.expiresAt ? ` – ${fmt(block.expiresAt)}` : ' (indefinite)';
    return `Scheduled: ${start}${end}`;
  }
  if (!block.expiresAt) return 'Indefinite';
  return `Until ${fmt(block.expiresAt)}`;
}

function gen15MinTimeOptions() {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      const suffix = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      opts.push({ value: `${hh}:${mm}`, label: `${h12}:${mm} ${suffix}` });
    }
  }
  return opts;
}

const TIME_OPTIONS = gen15MinTimeOptions();

const DURATION_OPTIONS = [
  { value: 15, label: '15 min' }, { value: 30, label: '30 min' },
  { value: 45, label: '45 min' }, { value: 60, label: '1 hr' },
  { value: 90, label: '1 hr 30 min' }, { value: 120, label: '2 hrs' },
  { value: 150, label: '2 hrs 30 min' }, { value: 180, label: '3 hrs' },
  { value: 240, label: '4 hrs' }, { value: 300, label: '5 hrs' },
  { value: 360, label: '6 hrs' }, { value: 480, label: '8 hrs' },
  { value: 600, label: '10 hrs' }, { value: 720, label: '12 hrs' },
  { value: 1440, label: '24 hrs' },
];

function localDatetimeToISO(dateStr, timeStr) {
  // dateStr: YYYY-MM-DD, timeStr: HH:MM → local datetime as ISO
  if (!dateStr || !timeStr) return null;
  const [hh, mm] = timeStr.split(':');
  const d = new Date(dateStr + 'T00:00:00');
  d.setHours(Number(hh), Number(mm), 0, 0);
  return d.toISOString();
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function nearestNext15Min() {
  const d = new Date();
  d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

function checkBlockConflicts(rawReservations, tableId, reservationLength, blockStartMs, blockEndMs) {
  return rawReservations.filter(r => {
    if (CLOSED_STATUSES.has(r.status)) return false;
    const tid = String(tableId);
    const touches = String(r.tableId) === tid || (Array.isArray(r.tableIds) && r.tableIds.includes(tid));
    if (!touches) return false;
    const resStartMs = r.date?.toMillis ? r.date.toMillis() : (r.date?.seconds ? r.date.seconds * 1000 : null);
    if (!resStartMs) return false;
    const resEndMs = resStartMs + (r.customDurationMinutes ?? reservationLength) * 60000;
    if (resEndMs <= blockStartMs) return false;
    if (blockEndMs !== null && resStartMs >= blockEndMs) return false;
    return true;
  });
}

function generateReservationId() {
  const ALPHA = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from({ length: 6 }, () => ALPHA[Math.floor(Math.random() * ALPHA.length)]).join('');
}

function parseStartMs(rDate, timeStr) {
  const parts = timeStr.split(/[: ]/);
  let h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const ampm = parts[2]?.toUpperCase();
  if (ampm === 'PM' && h !== 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return new Date(rDate.getFullYear(), rDate.getMonth(), rDate.getDate(), h, m).getTime();
}

// Statuses that mean a party is physically on premises — these must NEVER be
// filtered by any date or time window. A table occupied by a real party cannot
// become "available" just because the clock ticked past their slot.
const PHYSICAL_STATUSES = new Set(['seated', 'unseated', 'paid', 'bussing']);

function processReservations(raw, reservationLength, now) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return raw.map(r => {
    // Exclude reservations that no longer occupy the table
    if (CLOSED_STATUSES.has(r.status)) return null;

    // ── Bar seat tabs: no time-window filtering — active while status is live ──
    if (r.isBarSeat) {
      const BAR_ACTIVE = new Set(['seated', 'paid', 'bussing']);
      if (!BAR_ACTIVE.has(r.status)) return null;
      return { ...r, blocked: true, happeningNow: true, status: r.status, isLateUnseated: false, startMs: now, endMs: Infinity };
    }

    // ── Physically-present parties: bypass time-window filtering ──────────────
    // seated/unseated/paid/bussing means a real party is on premises. We skip
    // the time-window expiry check so an overstaying party never disappears.
    // However we still gate on date: only today's and yesterday's reservations
    // qualify — yesterday covers restaurants open past midnight where a party
    // seated at 11 PM may still be present after the clock rolls over.
    // Stale unclosed reservations from prior days are excluded (they should be
    // resolved at shift-close, not permanently blocking the floor map).
    if (PHYSICAL_STATUSES.has(r.status)) {
      const rDate = r.date?.toDate?.();
      if (rDate) {
        const rDay = new Date(rDate); rDay.setHours(0, 0, 0, 0);
        const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
        const isRecentDay = rDay.getTime() === today.getTime() || rDay.getTime() === yesterday.getTime();
        if (!isRecentDay) return null;
      }
      const startMs  = rDate ? parseStartMs(rDate, r.time) : (now - reservationLength * 60_000);
      const durationMs = (r.customDurationMinutes ?? reservationLength) * 60_000;
      const endMs    = startMs + durationMs;
      const isOverstay      = now >= endMs;
      const overstayMinutes = isOverstay ? Math.floor((now - endMs) / 60_000) : 0;
      return {
        ...r,
        blocked: true,
        happeningNow: true,
        status: r.status,
        isLateUnseated: false,
        isOverstay,
        overstayMinutes,
        startMs,
        endMs,
      };
    }

    // ── Standard time-window logic for upcoming/other statuses ─────────────────
    const rDate = r.date?.toDate?.();
    if (!rDate) return null;

    const rDay = new Date(rDate);
    rDay.setHours(0, 0, 0, 0);
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    const isToday     = rDay.getTime() === today.getTime();
    const isYesterday = rDay.getTime() === yesterday.getTime();
    if (!isToday && !isYesterday) return null;

    const startMs    = parseStartMs(rDate, r.time);
    const durationMs = (r.customDurationMinutes ?? reservationLength) * 60_000;
    const endMs      = startMs + durationMs;

    const isExpired = endMs < now - 60_000;
    if (isExpired) return null;

    const blockingWindowStart = startMs - durationMs;
    const isHappeningNow = startMs <= now && now < endMs;
    const isUpcoming     = now >= blockingWindowStart && now < startMs;
    if (!isHappeningNow && !isUpcoming) return null;

    const isUnseated     = isHappeningNow && !r.seatedTime;
    const isLateUnseated = isUnseated && (now - startMs) > 15 * 60_000;

    let status = r.status;
    if (!status || PHYSICAL_STATUSES.has(status)) {
      // Shouldn't reach here (physical statuses handled above), but guard anyway
      if (isUnseated)      status = 'unseated';
      else if (isHappeningNow) status = 'seated';
      else if (isUpcoming) status = 'upcoming';
    }

    return {
      ...r,
      blocked: ['seated', 'unseated', 'upcoming', 'paid', 'bussing'].includes(status),
      happeningNow: isHappeningNow,
      status,
      isLateUnseated,
      isOverstay: false,
      overstayMinutes: 0,
      startMs,
      endMs,
    };
  }).filter(Boolean);
}

function getBubbles(reservation, itemsMeta) {
  if (!reservation?.items?.length) return [];
  const pending = reservation.items.filter(i => i.itemStatus === 'pending');
  if (!pending.length) return [];

  const hasDrink   = pending.some(i => itemsMeta.find(m => m.name === i.name)?.isDrinkItem);
  const hasBar     = pending.some(i => itemsMeta.find(m => m.name === i.name)?.isBarItem);
  const hasDessert = pending.some(i => itemsMeta.find(m => m.name === i.name)?.isDessertItem);
  const hasFood    = pending.some(i => {
    const meta = itemsMeta.find(m => m.name === i.name);
    return meta && !meta.isDrinkItem && !meta.isBarItem && !meta.isDessertItem;
  });

  return [
    hasDrink   && { label: 'D',  color: '#1E90FF', title: 'Drink pending'   },
    hasBar     && { label: 'B',  color: '#9f207a', title: 'Bar pending'     },
    hasFood    && { label: 'F',  color: '#d07f00', title: 'Food pending'    },
    hasDessert && { label: 'DS', color: '#DA70D6', title: 'Dessert pending' },
  ].filter(Boolean);
}

// ─── Table colours (matching POS palette) ─────────────────────────────────────

const TABLE_COLORS = {
  seated:   '#a60b0b',
  unseated: '#c96d00',
  upcoming: '#445971',
  paid:     '#b45309',  // amber — paid, guests still lingering
  bussing:  '#374151',  // gray  — guests left, being cleared
  open:     '#348902',
};

// ─── Table photo modal constants ──────────────────────────────────────────────

const CROP_W   = 400;                           // display width of crop frame (px)
const CROP_H   = Math.round(CROP_W / (16 / 9)); // = 225  (16:9 aspect)
const OUT_W    = 1200;                          // output JPEG width
const OUT_H    = 675;                           // output JPEG height  (16:9)
const MIN_WARN_W = 640;                         // warn if source < this wide
const MIN_WARN_H = 360;

// ─── Table photo modal ────────────────────────────────────────────────────────

function TablePhotoModal({ table, restaurantId, restaurantDoc, onClose, onSaved }) {
  const fileRef   = useRef(null);
  const camRef    = useRef(null);
  const dragRef   = useRef({ x: 0, y: 0, px: 0, py: 0 });
  const touchRef  = useRef(null);

  const [phase,    setPhase]    = useState('preview'); // 'preview' | 'crop'
  const [imgSrc,   setImgSrc]   = useState(null);      // data-URL for crop
  const [natW,     setNatW]     = useState(0);
  const [natH,     setNatH]     = useState(0);
  const [panX,     setPanX]     = useState(0);
  const [panY,     setPanY]     = useState(0);
  const [scale,    setScale]    = useState(1);
  const [dragging, setDragging] = useState(false);
  const [sizeWarn, setSizeWarn] = useState(false);
  const [uploading,setUploading]= useState(false);
  const [error,    setError]    = useState('');

  const existingUrl = table.tablePicture || null;

  const clamp = useCallback((px, py, sc, nW, nH) => {
    const dW = nW * sc;
    const dH = nH * sc;
    const minX = dW > CROP_W ? CROP_W - dW : (CROP_W - dW) / 2;
    const maxX = dW > CROP_W ? 0           : (CROP_W - dW) / 2;
    const minY = dH > CROP_H ? CROP_H - dH : (CROP_H - dH) / 2;
    const maxY = dH > CROP_H ? 0           : (CROP_H - dH) / 2;
    return [
      Math.max(minX, Math.min(maxX, px)),
      Math.max(minY, Math.min(maxY, py)),
    ];
  }, []);

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { setError('File too large (max 15 MB).'); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      const img = new Image();
      img.onload = () => {
        const nW = img.naturalWidth;
        const nH = img.naturalHeight;
        setNatW(nW); setNatH(nH);
        setSizeWarn(nW < MIN_WARN_W || nH < MIN_WARN_H);
        const minSc = Math.max(CROP_W / nW, CROP_H / nH);
        const dW = nW * minSc;
        const dH = nH * minSc;
        setScale(minSc);
        setPanX((CROP_W - dW) / 2);
        setPanY((CROP_H - dH) / 2);
        setImgSrc(dataUrl);
        setError('');
        setPhase('crop');
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  // ── Mouse pan ──────────────────────────────────────────────────────────────
  const onMouseDown = (e) => {
    e.preventDefault();
    setDragging(true);
    dragRef.current = { x: e.clientX, y: e.clientY, px: panX, py: panY };
  };
  const onMouseMove = useCallback((e) => {
    if (!dragging) return;
    const [cx, cy] = clamp(
      dragRef.current.px + (e.clientX - dragRef.current.x),
      dragRef.current.py + (e.clientY - dragRef.current.y),
      scale, natW, natH,
    );
    setPanX(cx); setPanY(cy);
  }, [dragging, scale, natW, natH, clamp]);
  const onMouseUp = () => setDragging(false);

  // ── Touch pan ─────────────────────────────────────────────────────────────
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, px: panX, py: panY };
  };
  const onTouchMove = (e) => {
    if (!touchRef.current) return;
    e.preventDefault();
    const t = e.touches[0];
    const [cx, cy] = clamp(
      touchRef.current.px + (t.clientX - touchRef.current.x),
      touchRef.current.py + (t.clientY - touchRef.current.y),
      scale, natW, natH,
    );
    setPanX(cx); setPanY(cy);
  };
  const onTouchEnd = () => { touchRef.current = null; };

  // ── Wheel zoom ────────────────────────────────────────────────────────────
  const onWheel = (e) => {
    e.preventDefault();
    const minSc  = Math.max(CROP_W / natW, CROP_H / natH);
    const newSc  = Math.max(minSc, Math.min(scale * (e.deltaY > 0 ? 0.9 : 1.1), minSc * 5));
    const [cx, cy] = clamp(panX, panY, newSc, natW, natH);
    setScale(newSc); setPanX(cx); setPanY(cy);
  };

  // ── Save (crop → Storage → Firestore) ────────────────────────────────────
  const handleSave = async () => {
    setUploading(true); setError('');
    try {
      const canvas = document.createElement('canvas');
      canvas.width  = OUT_W;
      canvas.height = OUT_H;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = imgSrc; });
      ctx.drawImage(img, -panX / scale, -panY / scale, CROP_W / scale, CROP_H / scale, 0, 0, OUT_W, OUT_H);
      const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.9));
      const sRef = storageRef(storage, `${restaurantId}/table_photos/${table.tableId}`);
      const snap = await uploadBytes(sRef, blob, { contentType: 'image/jpeg' });
      const url  = await getDownloadURL(snap.ref);
      const updatedTables = (restaurantDoc?.tables || []).map(t =>
        String(t.tableId) === String(table.tableId) ? { ...t, tablePicture: url } : t
      );
      await updateDoc(doc(db, 'restaurants_data', restaurantId), { tables: updatedTables });
      onSaved?.(String(table.tableId), url);
      onClose();
    } catch (e) {
      console.error('[TablePhotoModal] save error:', e);
      setError('Upload failed — please try again.');
    } finally {
      setUploading(false);
    }
  };

  // ── Remove ────────────────────────────────────────────────────────────────
  const handleRemove = async () => {
    setUploading(true); setError('');
    try {
      const updatedTables = (restaurantDoc?.tables || []).map(t =>
        String(t.tableId) === String(table.tableId) ? { ...t, tablePicture: null } : t
      );
      await updateDoc(doc(db, 'restaurants_data', restaurantId), { tables: updatedTables });
      onSaved?.(String(table.tableId), null);
      onClose();
    } catch (e) {
      setError('Remove failed — please try again.');
    } finally {
      setUploading(false);
    }
  };

  const tableLabel = table.isBarSeat
    ? (table.label || `Bar ${table.seatId || table.tableId}`)
    : `Table ${table.tableId}`;
  const seatCount = table.seats ?? table.seatCount ?? null;

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 bg-black/60 backdrop-blur-sm">
        <div
          className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          style={{ width: '100%', maxWidth: 480, maxHeight: '90vh' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100 shrink-0">
            <div>
              <p className="text-sm font-semibold text-neutral-800">Edit Table Photo</p>
              <p className="text-xs text-neutral-400 mt-0.5">
                {tableLabel}{seatCount != null ? ` · ${seatCount} seats` : ''}
              </p>
            </div>
            <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700 text-xl leading-none">✕</button>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

            {/* Preview phase */}
            {phase === 'preview' && (
              <div className="space-y-4">
                {existingUrl ? (
                  <div className="rounded-xl overflow-hidden border border-neutral-200" style={{ aspectRatio: '16/9' }}>
                    <img src={existingUrl} alt="Current table" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div
                    className="rounded-xl border-2 border-dashed border-neutral-200 flex flex-col items-center justify-center text-neutral-400"
                    style={{ aspectRatio: '16/9' }}
                  >
                    <span className="text-3xl mb-1">📷</span>
                    <p className="text-sm">No photo yet</p>
                  </div>
                )}

                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="flex-1 px-4 py-2.5 text-sm font-medium border border-neutral-300 rounded-xl hover:border-neutral-500 hover:bg-neutral-50 transition-colors"
                  >
                    📁 Upload Photo
                  </button>
                  <button
                    onClick={() => camRef.current?.click()}
                    className="flex-1 px-4 py-2.5 text-sm font-medium border border-neutral-300 rounded-xl hover:border-neutral-500 hover:bg-neutral-50 transition-colors"
                  >
                    📷 Camera
                  </button>
                </div>

                {/* Hidden file inputs */}
                <input ref={fileRef} type="file" accept="image/*" className="hidden"
                  onChange={e => handleFile(e.target.files?.[0])} />
                <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={e => handleFile(e.target.files?.[0])} />

                {existingUrl && (
                  <button
                    onClick={handleRemove}
                    disabled={uploading}
                    className="w-full px-4 py-2 text-sm text-red-600 border border-red-200 rounded-xl hover:bg-red-50 disabled:opacity-50 transition-colors"
                  >
                    {uploading ? 'Removing…' : 'Remove Current Photo'}
                  </button>
                )}
              </div>
            )}

            {/* Crop phase */}
            {phase === 'crop' && imgSrc && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Crop Photo</p>
                  <p className="text-xs text-neutral-400">Drag to reposition · scroll to zoom · 16:9 ratio</p>
                </div>

                {sizeWarn && (
                  <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    ⚠ Small image — may appear blurry. Recommend at least 640 × 360 px.
                  </p>
                )}

                {/* Crop frame */}
                <div
                  style={{
                    width: CROP_W,
                    height: CROP_H,
                    overflow: 'hidden',
                    position: 'relative',
                    border: '2px solid #7c3aed',
                    borderRadius: 8,
                    cursor: dragging ? 'grabbing' : 'grab',
                    touchAction: 'none',
                    userSelect: 'none',
                    maxWidth: '100%',
                    margin: '0 auto',
                  }}
                  onMouseDown={onMouseDown}
                  onMouseMove={onMouseMove}
                  onMouseUp={onMouseUp}
                  onMouseLeave={onMouseUp}
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                  onWheel={onWheel}
                >
                  <img
                    src={imgSrc}
                    alt="crop preview"
                    draggable={false}
                    style={{
                      position: 'absolute',
                      left: panX,
                      top:  panY,
                      width:  natW * scale,
                      height: natH * scale,
                      pointerEvents: 'none',
                    }}
                  />
                  {/* Rule-of-thirds grid */}
                  <svg
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity: 0.25 }}
                    viewBox={`0 0 ${CROP_W} ${CROP_H}`}
                    preserveAspectRatio="none"
                  >
                    <line x1={CROP_W/3}   y1={0} x2={CROP_W/3}   y2={CROP_H} stroke="white" strokeWidth="1"/>
                    <line x1={CROP_W*2/3} y1={0} x2={CROP_W*2/3} y2={CROP_H} stroke="white" strokeWidth="1"/>
                    <line x1={0} y1={CROP_H/3}   x2={CROP_W} y2={CROP_H/3}   stroke="white" strokeWidth="1"/>
                    <line x1={0} y1={CROP_H*2/3} x2={CROP_W} y2={CROP_H*2/3} stroke="white" strokeWidth="1"/>
                  </svg>
                  {/* Corner handle dots */}
                  {[[0,0],[CROP_W,0],[0,CROP_H],[CROP_W,CROP_H]].map(([x,y],i) => (
                    <svg key={i} style={{ position: 'absolute', left: x-8, top: y-8, pointerEvents: 'none' }} width={16} height={16}>
                      <circle cx={8} cy={8} r={5} fill="#7c3aed" stroke="white" strokeWidth={1.5}/>
                    </svg>
                  ))}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => { setPhase('preview'); setImgSrc(null); }}
                    className="flex-1 px-4 py-2.5 text-sm font-medium border border-neutral-300 rounded-xl hover:bg-neutral-50 transition-colors"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="px-4 py-2.5 text-sm font-medium border border-neutral-300 rounded-xl hover:bg-neutral-50 transition-colors"
                  >
                    Change
                  </button>
                </div>

                {/* Hidden file inputs also needed in crop phase */}
                <input ref={fileRef} type="file" accept="image/*" className="hidden"
                  onChange={e => handleFile(e.target.files?.[0])} />
              </div>
            )}

            {error && (
              <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-4 border-t border-neutral-100 shrink-0 flex items-center justify-between">
            <button onClick={onClose} disabled={uploading} className="text-sm text-neutral-400 hover:text-neutral-700 disabled:opacity-50">
              Cancel
            </button>
            {phase === 'crop' && (
              <button
                onClick={handleSave}
                disabled={uploading}
                className="px-5 py-2 text-sm font-semibold bg-violet-600 text-white rounded-xl hover:bg-violet-700 disabled:opacity-50 transition-colors"
              >
                {uploading ? 'Saving…' : 'Save Photo'}
              </button>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}

// ─── Single table on the canvas ───────────────────────────────────────────────

function TableMapItem({ table, reservation, now, reservationLength, itemsMeta, dimScale, onSelect, block, editTablesMode, onEditTable, nextRes }) {
  const w        = (table.width  || 40) * dimScale;
  const h        = (table.height || 40) * dimScale;
  const rotation = table.rotation || 0;

  // When a reservation exists for this table, reservation visuals take priority over block visuals
  const isActiveBlock    = isBlockActive(block, now) && !reservation;
  const scheduledStartMs = block?.startsAt ? new Date(block.startsAt).getTime() : null;
  const isScheduledBlock = !isActiveBlock
    && !reservation
    && isBlockScheduled(block, now)
    && scheduledStartMs !== null
    && (scheduledStartMs - now) <= reservationLength * 60_000;

  const status      = reservation?.status ?? 'open';
  const isLate      = !isActiveBlock && reservation?.isLateUnseated;
  const isOverstay  = !isActiveBlock && reservation?.isOverstay;
  const bgColor     = isActiveBlock
    ? '#4b2d83'
    : isScheduledBlock
    ? '#5c3a0a'
    : isOverstay
    ? '#7f1d1d'
    : isLate ? '#cc0000' : (TABLE_COLORS[status] || TABLE_COLORS.open);
  const initials   = getInitials(reservation?.reservationServer);
  const bubbles    = reservation ? getBubbles(reservation, itemsMeta) : [];

  const resDurMs = ((reservation?.customDurationMinutes ?? reservationLength) * 60_000) || (reservationLength * 60_000);
  const frac = reservation?.happeningNow && !isOverstay
    ? Math.min(1, Math.max(0, (now - reservation.startMs) / resDurMs))
    : 0;
  const overstayFrac = isOverstay
    ? Math.min(1, (reservation.overstayMinutes || 0) / 30)
    : 0;

  // Font sizes scale with table size
  const fs1 = Math.max(7, Math.min(13, w * 0.22));
  const fs2 = Math.max(6, Math.min(11, w * 0.18));

  const BAR_H   = Math.max(3, 5 * dimScale); // height of horizontal bottom bar

  // Corner badge flags
  const hasCustomerNotes = !!reservation?.customerNotes;
  const hasResNote       = !!reservation?.reservationNote;
  const hasDrafts        = (reservation?.items || []).some(i => i.itemStatus === 'draft');

  // Status bubbles: overstay pill → ending soon → food/drink/etc → initials → upcoming time
  const isEndingSoon = reservation?.happeningNow && !isOverstay && frac >= 0.75;
  const upcomingBubbleRes = nextRes ?? (reservation?.status === 'upcoming' ? reservation : null);
  const bubbleItems = [
    ...(isOverstay ? [{ label: `+${reservation.overstayMinutes}m`, color: '#dc2626', isPill: true, title: 'Overstay' }] : []),
    ...(isEndingSoon ? [{ label: '!', color: '#b45309', title: 'Ending soon' }] : []),
    ...bubbles,
    ...(initials ? [{ label: initials, color: '#bbb', isInitials: true }] : []),
    ...(upcomingBubbleRes ? [{ label: fmt12(upcomingBubbleRes.time), color: '#1e3a5f', isPill: true, title: 'Upcoming reservation' }] : []),
  ];
  const bubbleSize   = Math.max(12, Math.min(18, w * 0.35));
  const bubbleOffset = (bubbleItems.length * (bubbleSize + 2)) / 2;

  return (
    <div style={{ position: 'absolute', top: `${table.topPct}%`, left: `${table.leftPct}%`, width: w, height: h, overflow: 'visible' }}>

      {/* Bubbles + server initials — above the table */}
      {bubbleItems.length > 0 && (
        <div style={{
          position: 'absolute',
          top: -bubbleSize - 3,
          left: w / 2 - bubbleOffset,
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          zIndex: 20,
        }}>
          {bubbleItems.map((b, i) => (
            <div
              key={i}
              title={b.title}
              style={{
                height: bubbleSize,
                minWidth: bubbleSize,
                paddingLeft: b.isPill ? Math.max(3, bubbleSize * 0.25) : 0,
                paddingRight: b.isPill ? Math.max(3, bubbleSize * 0.25) : 0,
                backgroundColor: b.color,
                borderRadius: bubbleSize / 2,
                border: '1px solid rgba(255,255,255,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <span style={{
                color: b.isInitials ? '#333' : 'white',
                fontSize: Math.max(6, bubbleSize * 0.55),
                fontWeight: 'bold',
                lineHeight: 1,
                userSelect: 'none',
                whiteSpace: 'nowrap',
              }}>
                {b.label}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Rotation wrapper — table body, pulse ring, and edit indicator all co-rotate */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: w,
        height: h,
        transform: `rotate(${rotation}deg)`,
        transformOrigin: 'center',
        overflow: 'visible',
      }}>

      {/* Table body */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: w,
        height: h,
        backgroundColor: bgColor,
        borderRadius: Math.max(4, 6 * dimScale),
        border: isActiveBlock
          ? `${Math.max(1, 2 * dimScale)}px solid rgba(139,92,246,0.6)`
          : isLate
          ? `${Math.max(1, 2 * dimScale)}px solid rgba(255,150,150,0.8)`
          : '1px solid rgba(255,255,255,0.25)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        cursor: 'pointer',
        userSelect: 'none',
      }}
      onClick={() => editTablesMode ? onEditTable?.(table) : onSelect && onSelect({ table, reservation })}>
        {isActiveBlock ? (
          <>
            <span style={{ color: 'rgba(196,181,253,0.9)', fontSize: fs1, fontWeight: 'bold', textAlign: 'center', lineHeight: 1.15 }}>
              #{table.tableId}
            </span>
            <span style={{ color: 'rgba(196,181,253,0.75)', fontSize: fs2, textAlign: 'center', lineHeight: 1.15 }}>
              Blocked
            </span>
          </>
        ) : isScheduledBlock ? (
          <>
            <span style={{ color: 'white', fontSize: fs1, fontWeight: 'bold', textAlign: 'center', lineHeight: 1.15 }}>
              #{table.tableId}
            </span>
            <span style={{ color: 'rgba(253,230,138,0.9)', fontSize: fs2, textAlign: 'center', lineHeight: 1.15 }}>
              Sched.
            </span>
          </>
        ) : reservation ? (
          <>
            <span style={{ color: 'white', fontSize: fs1, fontWeight: 'bold', textAlign: 'center', lineHeight: 1.15, padding: '0 2px' }}>
              {table.tableId} · {reservation.partySize}p
            </span>
            <span style={{ color: 'rgba(255,255,255,0.82)', fontSize: fs2, textAlign: 'center', lineHeight: 1.15 }}>
              {fmt12(reservation.time)}
            </span>
            {reservation.customerName && w > 48 && (
              <span style={{
                color: 'rgba(255,255,255,0.65)',
                fontSize: Math.max(6, fs2 - 1),
                textAlign: 'center',
                lineHeight: 1.1,
                overflow: 'hidden',
                maxWidth: '90%',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}>
                {reservation.customerName.split(' ')[0]}
              </span>
            )}
          </>
        ) : (
          <>
            <span style={{ color: 'white', fontSize: fs1, fontWeight: 'bold', textAlign: 'center', lineHeight: 1.15 }}>
              #{table.tableId}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: fs2, textAlign: 'center', lineHeight: 1.15 }}>
              {table.seats}s
            </span>
          </>
        )}

        {/* Paid/bussing status strip at the bottom of the table card */}
        {(status === 'paid' || status === 'bussing') && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            padding: '1px 0',
            backgroundColor: status === 'paid' ? 'rgba(180,83,9,0.85)' : 'rgba(55,65,81,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <span style={{ color: 'white', fontSize: Math.max(6, fs2 - 1), fontWeight: 900, letterSpacing: 0.5, userSelect: 'none' }}>
              {status === 'paid' ? '$ PAID' : '⊘ BUSSING'}
            </span>
          </div>
        )}

        {/* Countdown bar — bottom edge; overstay = growing red, normal = depleting blue→red */}
        {reservation?.happeningNow && status !== 'paid' && status !== 'bussing' && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: BAR_H,
            backgroundColor: 'rgba(0,0,0,0.3)',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: isOverstay ? `${overstayFrac * 100}%` : `${(1 - frac) * 100}%`,
              height: '100%',
              backgroundColor: isOverstay ? '#dc2626' : frac > 0.85 ? '#ef4444' : frac > 0.65 ? '#f59e0b' : '#1d4ed8',
            }} />
          </div>
        )}
      </div>

      {/* Late-unseated pulse ring — inside rotation wrapper so it co-rotates */}
      {isLate && (
        <div style={{
          position: 'absolute',
          top: -3,
          left: -3,
          width: w + 6,
          height: h + 6,
          borderRadius: Math.max(4, 8 * dimScale),
          border: '2px solid rgba(220,50,50,0.7)',
          animation: 'pulse 1.2s ease-in-out infinite',
          pointerEvents: 'none',
          zIndex: 5,
        }} />
      )}

      {/* Edit-mode indicator — inside rotation wrapper so it co-rotates */}
      {editTablesMode && (
        <div style={{
          position: 'absolute',
          inset: 0,
          border: '2px dashed #7c3aed',
          borderRadius: 4,
          pointerEvents: 'none',
          zIndex: 30,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <span style={{
            background: 'rgba(124,58,237,0.85)',
            color: 'white',
            fontSize: Math.max(8, Math.min(13, w * 0.24)),
            fontWeight: 700,
            borderRadius: 3,
            padding: '1px 4px',
            pointerEvents: 'none',
          }}>✎</span>
        </div>
      )}
      </div>{/* end rotation wrapper */}

      {/* Corner badges — top-right (customer profile note, reservation note, unsent drafts) */}
      {(hasCustomerNotes || hasResNote || hasDrafts) && (
        <div style={{
          position: 'absolute',
          top: -10,
          right: -4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          zIndex: 55,
          pointerEvents: 'none',
        }}>
          {hasCustomerNotes && (
            <div title="Customer profile notes" style={{ width: 14, height: 14, backgroundColor: '#C9A84C', borderRadius: 7, border: '1.5px solid #fde68a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: 'white', fontSize: 8, fontWeight: '900', lineHeight: 1 }}>★</span>
            </div>
          )}
          {hasResNote && (
            <div title="Reservation note" style={{ width: 14, height: 14, backgroundColor: '#0369a1', borderRadius: 3, border: '1px solid #bae6fd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: 'white', fontSize: 8, fontWeight: '900', lineHeight: 1 }}>≡</span>
            </div>
          )}
          {hasDrafts && (
            <div title="Unsent draft items" style={{ width: 14, height: 14, backgroundColor: '#0891b2', borderRadius: 7, border: '1px solid #a5f3fc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: 'white', fontSize: 8, fontWeight: '900', lineHeight: 1 }}>✎</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Bar seat stool on the canvas ─────────────────────────────────────────────

function BarSeatMapItem({ seat, reservation, dimScale, onSelect }) {
  const size    = (seat.width || 50) * dimScale;
  const label   = seat.label || `Bar ${seat.seatId}`;
  const status  = reservation?.status ?? 'open';

  const bgColor = status === 'paid'    ? '#b45309'
    : status === 'bussing' ? '#374151'
    : status === 'seated'  ? '#a60b0b'
    : '#1a6b6b'; // open / teal

  const fs1 = Math.max(7, size * 0.18);
  const fs2 = Math.max(6, size * 0.14);

  return (
    <div
      style={{ position: 'absolute', top: `${seat.topPct}%`, left: `${seat.leftPct}%`, width: size, height: size, overflow: 'visible' }}
    >
      <div
        title={label}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: bgColor,
          border: '2px solid rgba(255,255,255,0.22)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          overflow: 'hidden',
          position: 'relative',
        }}
        onClick={() => onSelect && onSelect({ table: { ...seat, tableId: seat.seatId, seats: 1, isBarSeat: true }, reservation })}
      >
        {reservation ? (
          <>
            <span style={{ color: 'white', fontSize: fs1, fontWeight: 'bold', textAlign: 'center', lineHeight: 1.15, padding: '0 2px' }}>
              {reservation.customerName?.split(' ')[0] || label}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.82)', fontSize: fs2, textAlign: 'center', lineHeight: 1.15 }}>
              {fmt12(reservation.time)}
            </span>
            {(status === 'paid' || status === 'bussing') && (
              <div style={{
                position: 'absolute', bottom: 0, left: 0, width: '100%', padding: '1px 0',
                backgroundColor: status === 'paid' ? 'rgba(180,83,9,0.88)' : 'rgba(55,65,81,0.88)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ color: 'white', fontSize: Math.max(5, size * 0.11), fontWeight: 900, userSelect: 'none' }}>
                  {status === 'paid' ? '$ PAID' : '⊘ DONE'}
                </span>
              </div>
            )}
          </>
        ) : (
          <>
            <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: Math.max(14, size * 0.38), fontWeight: 300, lineHeight: 1 }}>+</span>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: Math.max(5, size * 0.15), textAlign: 'center', lineHeight: 1.15 }}>
              {label}
            </span>
          </>
        )}
      </div>

      {/* Small teal dot — bar seat indicator */}
      <div style={{
        position: 'absolute', top: -3, right: -3,
        width: Math.max(7, size * 0.18), height: Math.max(7, size * 0.18),
        borderRadius: '50%', backgroundColor: '#1a9090',
        border: '1px solid rgba(255,255,255,0.5)', zIndex: 10, pointerEvents: 'none',
      }} />
    </div>
  );
}

// ─── Canvas map ───────────────────────────────────────────────────────────────

// Positions are stored as percentage strings ("39%", "35%") relative to the
// canvas. Width/height are pixel values authored against a ~750px-wide reference
// screen (matches the POS widthScaleFactor baseline of 750).
const POS_REFERENCE_W = 750;

function TableMapCanvas({ tables, resByTable, barSeats, barResByTable, now, reservationLength, itemsMeta, restaurantImage, onSelect, tableBlocks, editTablesMode, onEditTable, canEditTables, onToggleEditMode, nextResByTable }) {
  const wrapRef = useRef(null);
  const [containerW, setContainerW] = useState(0);
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useLayoutEffect(() => {
    if (wrapRef.current) setContainerW(wrapRef.current.offsetWidth);
  }, []);

  useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new ResizeObserver(entries => {
      setContainerW(Math.floor(entries[0].contentRect.width));
    });
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const goOnline  = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online',  goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online',  goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Parse percentage positions — "39%" → 39
  const parsed = useMemo(() => tables.map(t => {
    try {
      const raw = typeof t.position === 'string' ? JSON.parse(t.position) : t.position;
      const topPct  = parseFloat(raw?.top);
      const leftPct = parseFloat(raw?.left);
      if (isNaN(topPct) || isNaN(leftPct)) return null;
      return { ...t, topPct, leftPct };
    } catch { return null; }
  }).filter(Boolean), [tables]);

  const parsedBarSeats = useMemo(() => (barSeats || []).map(s => {
    try {
      const raw = typeof s.position === 'string' ? JSON.parse(s.position) : s.position;
      const topPct  = parseFloat(raw?.top);
      const leftPct = parseFloat(raw?.left);
      if (isNaN(topPct) || isNaN(leftPct)) return null;
      return { ...s, topPct, leftPct };
    } catch { return null; }
  }).filter(Boolean), [barSeats]);

  // Canvas maintains a strict 4:3 ratio.
  // Bump the max height to 75vh, then cap the width so the ratio never breaks.
  const maxH = typeof window !== 'undefined' ? window.innerHeight * 0.75 : 560;
  const maxW = Math.floor(maxH / 0.75);
  const effectiveW = containerW > 0 ? Math.min(containerW, maxW) : containerW;
  const canvasHeight = effectiveW > 0 ? effectiveW * 0.75 : 400;

  // Scale pixel dimensions relative to POS reference width
  const dimScale = effectiveW > 0 ? effectiveW / POS_REFERENCE_W : 1;

  return (
    <div ref={wrapRef} className="w-full flex justify-center">
      <div
        className="relative lg:rounded-xl border-y lg:border border-neutral-700 bg-neutral-900"
        style={{ width: effectiveW || '100%', height: canvasHeight || 400 }}
      >
        {/* Edit Tables button — anchored inside canvas top-right corner */}
        {canEditTables && (
          <button
            onClick={onToggleEditMode}
            className={`absolute top-2 right-2 z-20 text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${
              editTablesMode
                ? 'bg-violet-600 border-violet-500 text-white'
                : 'bg-neutral-900/80 border-neutral-600 text-neutral-400 hover:text-neutral-200 hover:border-neutral-400'
            }`}
          >
            {editTablesMode ? '✕ Exit Edit Mode' : '✎ Edit Tables'}
          </button>
        )}

        {/* Restaurant logo — top-left, matching POS position */}
        {restaurantImage && (
          <img
            src={restaurantImage}
            alt=""
            style={{
              position: 'absolute',
              top:    canvasHeight * 0.02,
              left:   effectiveW * 0.02,
              width:  effectiveW * 0.12,
              height: canvasHeight * 0.14,
              objectFit: 'contain',
              opacity: 0.75,
              zIndex: 5,
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Live / Offline indicator — top-center */}
        <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 border border-white/10 text-sm font-medium backdrop-blur-sm ${isOnline ? 'text-green-400' : 'text-amber-400'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-green-400 animate-pulse' : 'bg-amber-400'}`} />
            {isOnline ? 'Live' : 'Offline'}
          </span>
        </div>

        {parsed.map(table => (
          <TableMapItem
            key={table.tableId}
            table={table}
            reservation={resByTable[String(table.tableId)] ?? null}
            now={now}
            reservationLength={reservationLength}
            itemsMeta={itemsMeta}
            dimScale={dimScale}
            onSelect={onSelect}
            block={tableBlocks?.[String(table.tableId)] ?? null}
            editTablesMode={editTablesMode}
            onEditTable={onEditTable}
            nextRes={nextResByTable?.[String(table.tableId)] ?? null}
          />
        ))}

        {parsedBarSeats.map(seat => (
          <BarSeatMapItem
            key={`bar_${seat.seatId}`}
            seat={seat}
            reservation={(barResByTable || {})[String(seat.seatId)] ?? null}
            dimScale={dimScale}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Table detail modal ───────────────────────────────────────────────────────

const STATUS_LABEL = {
  seated:   { text: 'Seated',         cls: 'bg-red-900/60 text-red-300 border-red-800/50'           },
  unseated: { text: 'Unseated',       cls: 'bg-amber-900/60 text-amber-300 border-amber-800/50'     },
  upcoming: { text: 'Upcoming',       cls: 'bg-[#445971]/60 text-[#7fa3cc] border-[#5f799a]/40'     },
  paid:     { text: 'Paid — Lingering', cls: 'bg-amber-900/60 text-amber-300 border-amber-800/50'    },
  bussing:  { text: 'Being Cleared',  cls: 'bg-neutral-700/60 text-neutral-300 border-neutral-600/50' },
  open:     { text: 'Available',      cls: 'bg-green-900/50 text-green-400 border-green-800/40'     },
};

const ITEM_CATEGORIES = [
  { key: 'food',    label: 'Food',    test: (m) =>  m && !m.isDrinkItem && !m.isBarItem && !m.isDessertItem },
  { key: 'drink',   label: 'Drinks',  test: (m) =>  m?.isDrinkItem   },
  { key: 'bar',     label: 'Bar',     test: (m) =>  m?.isBarItem     },
  { key: 'dessert', label: 'Desserts',test: (m) =>  m?.isDessertItem },
];

function TableDetailModal({ table, reservation, reservationLength, now, itemsMeta, restaurantId, restaurantDoc, user, rawReservations, onClose }) {
  const [updating, setUpdating] = React.useState(false);

  const handleStatusUpdate = async (newStatus) => {
    if (!reservation?.id || !restaurantId) return;
    setUpdating(true);
    try {
      const payload = { status: newStatus };
      if (newStatus === 'seated' && !reservation.seatedTime) payload.seatedTime = Timestamp.now();
      await updateDoc(
        doc(db, 'restaurants_data', restaurantId, 'reservations', reservation.id),
        payload,
      );
    } catch (e) {
      console.error('[TableDetailModal] status update failed', e);
    } finally {
      setUpdating(false);
    }
  };

  const status      = reservation?.status ?? 'open';
  const badge       = STATUS_LABEL[status] || STATUS_LABEL.open;
  const isLate      = reservation?.isLateUnseated;

  const blockEntry       = restaurantDoc?.tableBlocks?.[String(table.tableId)];
  const isTableBlocked   = isBlockActive(blockEntry, now) && !reservation;
  const isTableScheduled = !isTableBlocked && !reservation && isBlockScheduled(blockEntry, now);

  // Time elapsed fraction for the reservation
  const frac = reservation?.happeningNow
    ? Math.min(1, Math.max(0, (now - reservation.startMs) / (reservationLength * 60_000)))
    : 0;
  const elapsedMin  = reservation?.happeningNow ? Math.floor((now - reservation.startMs) / 60_000) : 0;
  const totalMin    = reservationLength;

  // Partition items into pending / sent-kitchen / completed
  const allItems = reservation?.items || [];
  const pending   = allItems.filter(i => i.itemStatus === 'pending');
  const sent      = allItems.filter(i => i.itemStatus === 'sent' || i.itemStatus === 'cooking');
  const completed = allItems.filter(i => i.itemStatus === 'completed' || i.itemStatus === 'served');

  // Group pending by category using itemsMeta
  const getMeta = (name) => itemsMeta.find(m => m.name === name);
  const grouped = ITEM_CATEGORIES.map(cat => ({
    ...cat,
    items: pending.filter(i => cat.test(getMeta(i.name))),
  })).filter(g => g.items.length > 0);

  const tableLabel = table.isBarSeat
    ? (table.label || `Bar ${table.tableId}`)
    : table.tableType && table.tableType !== 'no type'
    ? `${table.tableType} ${table.tableId}`
    : `Table ${table.tableId}`;

  return (
    <Portal>
      <div
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-0 sm:px-4 py-0 sm:py-6 bg-black/70"
        onClick={onClose}
      >
        <div
          className="bg-neutral-900 border border-neutral-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[90dvh] flex flex-col overflow-hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          onClick={e => e.stopPropagation()}
        >

          {/* Header */}
          <div className="shrink-0 px-5 py-4 border-b border-neutral-700 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div>
                <p className="text-base font-bold text-neutral-100 leading-tight">{tableLabel}</p>
                <p className="text-xs text-neutral-400">{table.seats} seats</p>
              </div>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${badge.cls} ${isLate ? '!bg-red-800/60 !text-red-200 !border-red-700/50' : ''}`}>
                {isLate ? 'Late — Unseated' : badge.text}
              </span>
              {isTableBlocked && (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full border bg-purple-900/60 text-purple-300 border-purple-700/50">
                  Blocked
                </span>
              )}
              {isTableScheduled && (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full border bg-amber-900/60 text-amber-300 border-amber-700/50">
                  Block Scheduled
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="shrink-0 text-neutral-400 hover:text-white text-2xl leading-none transition-colors"
            >×</button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5">

            {reservation ? (
              <>
                {/* Guest info */}
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Guest</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-neutral-800 rounded-xl px-4 py-3">
                      <p className="text-xs text-neutral-400 mb-0.5">Name</p>
                      <p className="text-sm font-semibold text-white">{reservation.customerName || '—'}</p>
                    </div>
                    <div className="bg-neutral-800 rounded-xl px-4 py-3">
                      <p className="text-xs text-neutral-400 mb-0.5">Party</p>
                      <p className="text-sm font-semibold text-white">{reservation.partySize ?? '—'} guests</p>
                    </div>
                    <div className="bg-neutral-800 rounded-xl px-4 py-3">
                      <p className="text-xs text-neutral-400 mb-0.5">Time</p>
                      <p className="text-sm font-semibold text-white">{fmt12(reservation.time)}</p>
                    </div>
                    <div className="bg-neutral-800 rounded-xl px-4 py-3">
                      <p className="text-xs text-neutral-400 mb-0.5">Server</p>
                      <p className="text-sm font-semibold text-white truncate">{reservation.reservationServer || '—'}</p>
                    </div>
                    {reservation.phone && (
                      <div className="bg-neutral-800 rounded-xl px-4 py-3 col-span-2">
                        <p className="text-xs text-neutral-400 mb-0.5">Phone</p>
                        <p className="text-sm font-semibold text-white">{reservation.phone}</p>
                      </div>
                    )}
                    {reservation.specialRequest && (
                      <div className="bg-neutral-800 rounded-xl px-4 py-3 col-span-2">
                        <p className="text-xs text-neutral-400 mb-0.5">Special Request</p>
                        <p className="text-sm text-neutral-300">{reservation.specialRequest}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Post-payment status banner */}
                {(status === 'paid' || status === 'bussing') && (() => {
                  const payments = Array.isArray(reservation.payments) ? reservation.payments : [];
                  const totalPaid = payments.reduce((sum, p) => sum + (p.totalCaptured ?? p.amount ?? 0), 0);
                  const totalTip  = payments.reduce((sum, p) => sum + (p.tipAmount ?? 0), 0);
                  return (
                    <div className={`rounded-xl border overflow-hidden ${
                      status === 'paid' ? 'border-amber-800/50' : 'border-neutral-600/50'
                    }`}>
                      {/* Banner row */}
                      <div className={`px-4 py-3 flex items-center gap-3 ${
                        status === 'paid' ? 'bg-amber-950/60' : 'bg-neutral-800'
                      }`}>
                        <span className="text-xl">{status === 'paid' ? '💳' : '🧹'}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-bold ${status === 'paid' ? 'text-amber-300' : 'text-neutral-200'}`}>
                            {status === 'paid' ? 'Paid — Guests Lingering' : 'Being Cleared'}
                          </p>
                          <p className="text-xs text-neutral-400 mt-0.5">
                            {status === 'paid'
                              ? 'Waiting for guests to leave before turning the table.'
                              : 'Guests have left. Table is being bussed.'}
                          </p>
                        </div>
                      </div>
                      {/* Payment summary */}
                      {payments.length > 0 && (
                        <div className="bg-neutral-900/60 px-4 py-2 flex gap-4 text-sm">
                          {payments.map((p, i) => (
                            <div key={i} className="flex items-center gap-1.5">
                              <span className="text-neutral-400 text-xs capitalize">{p.type || 'payment'}</span>
                              <span className="font-semibold text-white">${((p.totalCaptured ?? p.amount ?? 0)).toFixed(2)}</span>
                            </div>
                          ))}
                          {totalTip > 0 && (
                            <div className="flex items-center gap-1.5 ml-auto">
                              <span className="text-neutral-400 text-xs">tip</span>
                              <span className="font-semibold text-emerald-400">${totalTip.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1.5 ml-auto border-l border-neutral-700 pl-3">
                            <span className="text-neutral-400 text-xs">total</span>
                            <span className="font-bold text-white">${totalPaid.toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Time progress (only for active reservations) */}
                {reservation.happeningNow && status !== 'paid' && status !== 'bussing' && (
                  <div>
                    <div className="flex justify-between items-center mb-1.5">
                      <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Time</p>
                      <p className="text-xs text-neutral-300">
                        {elapsedMin}m elapsed · {Math.max(0, totalMin - elapsedMin)}m remaining
                      </p>
                    </div>
                    <div className="h-2 bg-neutral-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${frac * 100}%`,
                          backgroundColor: frac > 0.85 ? '#ef4444' : frac > 0.65 ? '#f59e0b' : '#5f799a',
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Items on order */}
                <div>
                  <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-2">Order</p>

                  {allItems.length === 0 ? (
                    <p className="text-sm text-neutral-400 italic">No items on order</p>
                  ) : (
                    <div className="flex flex-col gap-3">

                      {/* Pending — grouped by category */}
                      {grouped.length > 0 && grouped.map(cat => (
                        <div key={cat.key}>
                          <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1">{cat.label}</p>
                          <div className="flex flex-col gap-1">
                            {cat.items.map((item, i) => (
                              <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 bg-neutral-800 rounded-lg">
                                <span className="text-sm text-neutral-200">{item.name}</span>
                                {item.quantity > 1 && (
                                  <span className="text-xs text-neutral-400">×{item.quantity}</span>
                                )}
                                <span className="text-[10px] font-semibold text-amber-400 bg-amber-900/30 border border-amber-800/30 px-1.5 py-0.5 rounded">
                                  Pending
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}

                      {/* Sent / cooking */}
                      {sent.length > 0 && (
                        <div>
                          <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1">In Progress</p>
                          <div className="flex flex-col gap-1">
                            {sent.map((item, i) => (
                              <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 bg-neutral-800 rounded-lg">
                                <span className="text-sm text-neutral-200">{item.name}</span>
                                {item.quantity > 1 && <span className="text-xs text-neutral-400">×{item.quantity}</span>}
                                <span className="text-[10px] font-semibold text-blue-400 bg-blue-900/30 border border-blue-800/30 px-1.5 py-0.5 rounded">
                                  Sent
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Completed */}
                      {completed.length > 0 && (
                        <div>
                          <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1">Served</p>
                          <div className="flex flex-col gap-1">
                            {completed.map((item, i) => (
                              <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 bg-neutral-800/50 rounded-lg opacity-50">
                                <span className="text-sm text-neutral-300 line-through">{item.name}</span>
                                {item.quantity > 1 && <span className="text-xs text-neutral-400">×{item.quantity}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* Open table */
              <div className="flex flex-col items-center justify-center py-8 gap-2 text-center">
                <div className="w-12 h-12 rounded-full bg-green-900/40 border border-green-700/40 flex items-center justify-center mb-1">
                  <span className="text-green-400 text-xl">✓</span>
                </div>
                <p className="text-base font-semibold text-green-400">Available</p>
                <p className="text-sm text-neutral-400">{table.seats} seats · no active reservation</p>
              </div>
            )}

            {/* Status actions */}
            {reservation && (
              <div>
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-2">Actions</p>
                <div className="flex flex-wrap gap-2">
                  {(status === 'upcoming' || status === 'unseated') && (
                    <>
                      <button
                        disabled={updating}
                        onClick={() => handleStatusUpdate('seated')}
                        className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-red-700 hover:bg-red-600 text-white disabled:opacity-50 transition-colors"
                      >
                        Seat Party
                      </button>
                      <button
                        disabled={updating}
                        onClick={() => handleStatusUpdate('noShow')}
                        className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-neutral-700 hover:bg-neutral-600 text-neutral-200 disabled:opacity-50 transition-colors"
                      >
                        No Show
                      </button>
                    </>
                  )}
                  {status === 'seated' && (
                    <>
                      <button
                        disabled={updating}
                        onClick={() => handleStatusUpdate('paid')}
                        className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-amber-700 hover:bg-amber-600 text-white disabled:opacity-50 transition-colors"
                      >
                        Mark Paid
                      </button>
                      <button
                        disabled={updating}
                        onClick={() => handleStatusUpdate('comped')}
                        className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-neutral-700 hover:bg-neutral-600 text-neutral-200 disabled:opacity-50 transition-colors"
                      >
                        Comp
                      </button>
                      <button
                        disabled={updating}
                        onClick={() => handleStatusUpdate('walk_out')}
                        className="w-full py-2.5 rounded-xl text-sm font-semibold bg-neutral-800 hover:bg-neutral-700 text-neutral-400 disabled:opacity-50 transition-colors"
                      >
                        Walk Out
                      </button>
                    </>
                  )}
                  {status === 'paid' && (
                    <button
                      disabled={updating}
                      onClick={() => handleStatusUpdate('bussing')}
                      className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-neutral-700 hover:bg-neutral-600 text-neutral-200 disabled:opacity-50 transition-colors"
                    >
                      Start Bussing
                    </button>
                  )}
                  {status === 'bussing' && (
                    <button
                      disabled={updating}
                      onClick={() => handleStatusUpdate('completed')}
                      className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-teal-700 hover:bg-teal-600 text-white disabled:opacity-50 transition-colors"
                    >
                      Table Cleared
                    </button>
                  )}
                  {updating && (
                    <span className="text-xs text-neutral-500 self-center">Saving…</span>
                  )}
                </div>
              </div>
            )}

            {/* Block section */}
            <BlockTableSection
              table={table}
              restaurantId={restaurantId}
              restaurantDoc={restaurantDoc}
              user={user}
              rawReservations={rawReservations}
              reservationLength={reservationLength}
              now={now}
            />
          </div>
        </div>
      </div>
    </Portal>
  );
}

// ─── Block Table Section ──────────────────────────────────────────────────────

function BlockTableSection({ table, restaurantId, restaurantDoc, user, rawReservations, reservationLength, now }) {
  const canBlock = user?.role === 'owner' || user?.permissions?.blockTables === true;
  const tableBlocks = restaurantDoc?.tableBlocks || {};
  const block = tableBlocks[String(table.tableId)] || null;
  const active = isBlockActive(block, now);
  const scheduled = isBlockScheduled(block, now);
  const hasBlock = active || scheduled;

  const [mode, setMode] = useState(null); // null | 'form'
  const [blockType, setBlockType] = useState('indefinite'); // 'indefinite' | 'until' | 'allday' | 'window'
  const [reason, setReason] = useState('');
  const [untilDate, setUntilDate] = useState(todayStr());
  const [untilTime, setUntilTime] = useState(nearestNext15Min());
  const [allDayDate, setAllDayDate] = useState(() => { const d = new Date(); d.setDate(d.getDate()+1); return d.toISOString().split('T')[0]; });
  const [windowDate, setWindowDate] = useState(todayStr());
  const [windowTime, setWindowTime] = useState(nearestNext15Min());
  const [windowDuration, setWindowDuration] = useState(60);
  const [createRes, setCreateRes] = useState(false);
  const [resName, setResName] = useState('');
  const [resParty, setResParty] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const blockedBy = user?.firstName
    ? `${user.firstName} ${user.lastName || ''}`.trim()
    : (user?.role === 'owner' ? 'Owner' : 'Manager');

  // Compute block window for conflict check
  const blockWindow = (() => {
    const now2 = new Date();
    if (blockType === 'indefinite') return { startMs: now2.getTime(), endMs: null };
    if (blockType === 'until') {
      const iso = localDatetimeToISO(untilDate, untilTime);
      return iso ? { startMs: now2.getTime(), endMs: new Date(iso).getTime() } : null;
    }
    if (blockType === 'allday') {
      const start = new Date(allDayDate + 'T00:00:00');
      const end   = new Date(allDayDate + 'T23:59:59');
      return { startMs: start.getTime(), endMs: end.getTime() };
    }
    if (blockType === 'window') {
      const iso = localDatetimeToISO(windowDate, windowTime);
      if (!iso) return null;
      const startMs = new Date(iso).getTime();
      return { startMs, endMs: startMs + windowDuration * 60000 };
    }
    return null;
  })();

  const conflicts = blockWindow && rawReservations
    ? checkBlockConflicts(rawReservations, table.tableId, reservationLength, blockWindow.startMs, blockWindow.endMs)
    : [];

  const handleSave = async () => {
    if (!restaurantId) return;
    setSaving(true);
    setError('');
    try {
      const now2 = new Date();
      let startsAt = null;
      let expiresAt = null;

      if (blockType === 'indefinite') {
        // startsAt = now, no expiry
      } else if (blockType === 'until') {
        expiresAt = localDatetimeToISO(untilDate, untilTime);
      } else if (blockType === 'allday') {
        startsAt  = new Date(allDayDate + 'T00:00:00').toISOString();
        expiresAt = new Date(allDayDate + 'T23:59:59').toISOString();
      } else if (blockType === 'window') {
        const iso = localDatetimeToISO(windowDate, windowTime);
        if (iso) {
          startsAt  = iso;
          expiresAt = new Date(new Date(iso).getTime() + windowDuration * 60000).toISOString();
        }
      }

      const newBlock = {
        blockType,
        reason: reason.trim(),
        blockedAt: now2.toISOString(),
        blockedBy,
        startsAt:  startsAt || null,
        expiresAt: expiresAt || null,
      };

      await updateDoc(doc(db, 'restaurants_data', restaurantId), {
        [`tableBlocks.${table.tableId}`]: newBlock,
      });

      // Optionally create a reservation doc for visibility
      if (createRes && (blockType === 'allday' || blockType === 'window') && (resName.trim() || reason.trim())) {
        const resStartIso = startsAt || new Date().toISOString();
        const resDate = new Date(resStartIso);
        const hh = String(resDate.getHours()).padStart(2,'0');
        const mm = String(resDate.getMinutes()).padStart(2,'0');
        const timeStr = `${hh}:${mm}`;
        const dateOnly = resDate.toISOString().split('T')[0];
        const duration = blockType === 'window' ? windowDuration : 1440;
        const resDoc = {
          reservationId:  generateReservationId(),
          customerName:   resName.trim() || reason.trim() || 'Private Party',
          phone:          null,
          partySize:      resParty.trim() || '1',
          tableId:        String(table.tableId),
          time:           timeStr,
          date:           Timestamp.fromDate(resDate),
          dateOnly,
          requestDate:    dateOnly,
          items: [], total: 0, tip: 0,
          paid: false, paymentStatus: 'pending',
          stripeTransactionId: null,
          status: 'upcoming',
          seatedTime: null,
          reservationServer: null,
          employeeId: null,
          reservationNote: reason.trim() || null,
          canceledAt: null,
          WaitlistID: null,
          sms_reservationConfirmation: false,
          sms_tableReady: false,
          sms_reminderFollowUp: false,
          sms_autoDrop: false,
          tablePhotoUrl: null,
          payments: null,
          customDurationMinutes: duration,
        };
        await addDoc(collection(db, 'restaurants_data', restaurantId, 'reservations'), resDoc);
      }

      setMode(null);
    } catch (err) {
      setError('Failed to save block.');
      console.error('[BlockTableSection] save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUnblock = async () => {
    if (!restaurantId) return;
    setSaving(true);
    setError('');
    try {
      await updateDoc(doc(db, 'restaurants_data', restaurantId), {
        [`tableBlocks.${table.tableId}`]: deleteField(),
      });
    } catch (err) {
      setError('Failed to unblock.');
    } finally {
      setSaving(false);
    }
  };

  const INPUT = 'w-full bg-neutral-800 border border-neutral-600 text-neutral-200 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#5f799a]';
  const SELECT = 'bg-neutral-800 border border-neutral-600 text-neutral-200 text-sm rounded-lg px-2 py-2 focus:outline-none focus:border-[#5f799a]';

  return (
    <div className="border-t border-neutral-700 pt-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Table Availability</p>
        {!canBlock && (
          <span className="text-xs text-neutral-500">No permission</span>
        )}
      </div>

      {/* Current block status */}
      {hasBlock && (
        <div className={`mb-3 px-3 py-2.5 rounded-xl border ${active ? 'bg-red-900/20 border-red-800/40' : 'bg-amber-900/20 border-amber-800/40'}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className={`text-xs font-semibold ${active ? 'text-red-300' : 'text-amber-300'}`}>
                {active ? 'Blocked' : 'Scheduled Block'}
              </p>
              <p className="text-xs text-neutral-400 mt-0.5">{formatBlockExpiry(block)}</p>
              {block?.reason && <p className="text-xs text-neutral-500 mt-0.5">{block.reason}</p>}
              {block?.blockedBy && <p className="text-xs text-neutral-600 mt-0.5">By {block.blockedBy}</p>}
            </div>
            {canBlock && (
              <button
                onClick={handleUnblock}
                disabled={saving}
                className="shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg bg-neutral-700 text-neutral-200 hover:bg-neutral-600 disabled:opacity-50"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      )}

      {/* Block form trigger */}
      {canBlock && mode === null && (
        <button
          onClick={() => setMode('form')}
          className="w-full py-2 text-sm font-medium rounded-xl border border-neutral-700 text-neutral-300 hover:bg-neutral-800 transition-colors"
        >
          {hasBlock ? 'Edit Block' : 'Block this table'}
        </button>
      )}

      {/* Block form */}
      {canBlock && mode === 'form' && (
        <div className="space-y-4">
          {/* Block type selector */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: 'indefinite', label: 'Indefinitely' },
              { key: 'until',      label: 'Until date/time' },
              { key: 'allday',     label: 'All day (future date)' },
              { key: 'window',     label: 'Scheduled window' },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => setBlockType(opt.key)}
                className={`py-2 px-3 text-xs font-semibold rounded-lg border transition-colors text-left ${
                  blockType === opt.key
                    ? 'bg-[#5f799a] border-[#5f799a] text-white'
                    : 'bg-neutral-800 border-neutral-600 text-neutral-300 hover:border-neutral-500'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Type-specific inputs */}
          {blockType === 'until' && (
            <div className="flex gap-2">
              <input type="date" value={untilDate} min={todayStr()} onChange={e => setUntilDate(e.target.value)} className={INPUT + ' flex-1'} />
              <select value={untilTime} onChange={e => setUntilTime(e.target.value)} className={SELECT}>
                {TIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}

          {blockType === 'allday' && (
            <div>
              <label className="block text-xs text-neutral-400 mb-1">Date to block</label>
              <input type="date" value={allDayDate} min={todayStr()} onChange={e => setAllDayDate(e.target.value)} className={INPUT} />
            </div>
          )}

          {blockType === 'window' && (
            <div className="space-y-2">
              <div>
                <label className="block text-xs text-neutral-400 mb-1">Starts at</label>
                <div className="flex gap-2">
                  <input type="date" value={windowDate} min={todayStr()} onChange={e => setWindowDate(e.target.value)} className={INPUT + ' flex-1'} />
                  <select value={windowTime} onChange={e => setWindowTime(e.target.value)} className={SELECT}>
                    {TIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-neutral-400 mb-1">Duration</label>
                <select value={windowDuration} onChange={e => setWindowDuration(Number(e.target.value))} className={SELECT + ' w-full'}>
                  {DURATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-xs text-neutral-400 mb-1">Reason (optional)</label>
            <input
              type="text"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Private party, maintenance…"
              className={INPUT}
            />
          </div>

          {/* Conflict warning */}
          {conflicts.length > 0 && (
            <div className="px-3 py-2.5 rounded-xl bg-amber-900/20 border border-amber-700/40">
              <p className="text-xs font-semibold text-amber-300 mb-1">
                {conflicts.length} existing reservation{conflicts.length > 1 ? 's' : ''} in this window
              </p>
              {conflicts.map(r => (
                <p key={r.id} className="text-xs text-amber-400">
                  {r.customerName || '—'} · {r.time}
                </p>
              ))}
              <p className="text-xs text-neutral-500 mt-1">Existing reservations will still be honored.</p>
            </div>
          )}

          {/* Create reservation option (for allday / window) */}
          {(blockType === 'allday' || blockType === 'window') && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={createRes} onChange={e => setCreateRes(e.target.checked)} className="w-4 h-4 accent-[#5f799a]" />
              <span className="text-sm text-neutral-300">Create a reservation for this block</span>
            </label>
          )}

          {createRes && (blockType === 'allday' || blockType === 'window') && (
            <div className="space-y-2 pl-7">
              <input
                type="text"
                value={resName}
                onChange={e => setResName(e.target.value)}
                placeholder="Guest / party name"
                className={INPUT}
              />
              <input
                type="text"
                value={resParty}
                onChange={e => setResParty(e.target.value)}
                placeholder="Party size"
                className={INPUT}
              />
            </div>
          )}

          {error && <p className="text-xs text-red-400">{error}</p>}

          <div className="flex gap-2 pt-1">
            <button
              onClick={() => { setMode(null); setError(''); }}
              className="flex-1 py-2 text-sm font-medium rounded-xl bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-2 text-sm font-semibold rounded-xl bg-red-700 hover:bg-red-600 text-white disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Block Table'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Stats bar ────────────────────────────────────────────────────────────────

function StatsBar({ tables, resByTable, tableBlocks, now }) {
  const nowMs    = now ?? Date.now();
  const seated   = tables.filter(t => resByTable[String(t.tableId)]?.status === 'seated').length;
  const unseated = tables.filter(t => resByTable[String(t.tableId)]?.status === 'unseated').length;
  const upcoming = tables.filter(t => resByTable[String(t.tableId)]?.status === 'upcoming').length;
  const blocked  = tables.filter(t => isBlockActive(tableBlocks?.[String(t.tableId)], nowMs)).length;
  const open     = tables.length - seated - unseated - upcoming - blocked;

  const stats = [
    { label: 'Seated',    value: seated,   cls: 'text-red-400',    bg: 'bg-neutral-800 border-red-900/50'       },
    { label: 'Unseated',  value: unseated, cls: 'text-amber-400',  bg: 'bg-neutral-800 border-amber-900/50'     },
    { label: 'Upcoming',  value: upcoming, cls: 'text-[#7fa3cc]',  bg: 'bg-neutral-800 border-[#5f799a]/30'     },
    { label: 'Blocked',   value: blocked,  cls: 'text-purple-400', bg: 'bg-neutral-800 border-purple-900/50'    },
    { label: 'Available', value: open,     cls: 'text-green-400',  bg: 'bg-neutral-800 border-green-900/50'     },
  ];

  return (
    <div className="grid grid-cols-5 gap-1.5 px-4 lg:px-0">
      {stats.map(s => (
        <div key={s.label} className={`border rounded-xl px-1 py-2.5 text-center ${s.bg}`}>
          <p className={`text-lg font-bold leading-tight ${s.cls}`}>{s.value}</p>
          <p className="text-[10px] leading-tight text-neutral-300 truncate px-0.5">{s.label}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Legend ───────────────────────────────────────────────────────────────────

function Legend() {
  const statusItems = [
    { color: TABLE_COLORS.seated,   label: 'Seated'   },
    { color: TABLE_COLORS.unseated, label: 'Unseated' },
    { color: TABLE_COLORS.upcoming, label: 'Upcoming' },
    { color: '#4b2d83',             label: 'Blocked'  },
    { color: TABLE_COLORS.open,     label: 'Available'},
  ];
  const bubbleItems = [
    { label: 'D',  color: '#1E90FF', title: 'Drink'   },
    { label: 'B',  color: '#9f207a', title: 'Bar'     },
    { label: 'F',  color: '#d07f00', title: 'Food'    },
    { label: 'DS', color: '#DA70D6', title: 'Dessert' },
  ];

  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-neutral-400 px-4 lg:px-1">
      {/* Status */}
      <div className="flex items-center gap-3 flex-wrap">
        {statusItems.map(s => (
          <span key={s.label} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
      <span className="text-neutral-500">·</span>
      {/* Bubbles */}
      <div className="flex items-center gap-3 flex-wrap">
        {bubbleItems.map(b => (
          <span key={b.label} className="flex items-center gap-1">
            <span
              style={{ backgroundColor: b.color }}
              className="w-4 h-4 rounded-full inline-flex items-center justify-center text-[9px] font-bold text-white shrink-0"
            >
              {b.label}
            </span>
            {b.title}
          </span>
        ))}
      </div>
      <span className="flex items-center gap-1.5 shrink-0">
        <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
        Late unseated
      </span>
    </div>
  );
}

// ─── Reservation grid helpers ────────────────────────────────────────────────

function buildTimeIntervals(openTime, closeTime) {
  const intervals = [];
  const cur = new Date(openTime);
  const end = new Date(closeTime);
  while (cur < end) {
    intervals.push(
      `${String(cur.getHours()).padStart(2, '0')}:${String(cur.getMinutes()).padStart(2, '0')}`
    );
    cur.setMinutes(cur.getMinutes() + 15);
  }
  return intervals;
}

function getHoursForDate(date, restaurantDoc) {
  if (!restaurantDoc?.hours) return null;
  const { regularHours = [], customHours = [], holidays = [] } = restaurantDoc.hours;
  const dateStr = date.toISOString().split('T')[0];

  const holiday = holidays.find(h => {
    const hd = h.date?.toDate?.() || new Date(h.date);
    return hd.toISOString().split('T')[0] === dateStr;
  });
  if (holiday) return null;

  const custom = customHours.find(h => {
    const hd = h.openTime?.toDate?.() || new Date(h.openTime);
    return hd.toISOString().split('T')[0] === dateStr;
  });
  if (custom) {
    return {
      openTime:  custom.openTime?.toDate?.()  || new Date(custom.openTime),
      closeTime: custom.closeTime?.toDate?.() || new Date(custom.closeTime),
    };
  }

  const dayName = date.toLocaleString('en-US', { weekday: 'long' }).toLowerCase();
  const regular = regularHours.find(r => r.dayOfWeek === dayName);
  if (!regular || regular.isClosed) return null;

  const [oh, om] = regular.openTime.split(':').map(Number);
  const [ch, cm] = regular.closeTime.split(':').map(Number);
  const openTime  = new Date(date); openTime.setHours(oh, om, 0, 0);
  const closeTime = new Date(date); closeTime.setHours(ch, cm, 0, 0);
  return { openTime, closeTime };
}

function buildTimeTable(tables, reservations, reservationLength, intervals, tableBlocks = {}, selectedDate = new Date()) {
  const blockedSlots = Math.max(1, Math.round(reservationLength / 15));

  // Compute the selected day's window for block overlap check
  const selDayStart = new Date(selectedDate); selDayStart.setHours(0,  0,  0,   0);
  const selDayEnd   = new Date(selectedDate); selDayEnd.setHours(23, 59, 59, 999);
  const selDayStartMs = selDayStart.getTime();
  const selDayEndMs   = selDayEnd.getTime();

  function computeBlockedCellIndices(tableId) {
    const block = tableBlocks[String(tableId)];
    if (!block) return new Set();
    const blockStartMs = block.startsAt ? new Date(block.startsAt).getTime() : 0;
    const blockEndMs   = block.expiresAt ? new Date(block.expiresAt).getTime() : Infinity;
    if (blockStartMs >= selDayEndMs || blockEndMs <= selDayStartMs) return new Set();
    const indices = new Set();
    intervals.forEach((iv, idx) => {
      const [hh, mm] = iv.split(':').map(Number);
      const cellStart = new Date(selectedDate);
      cellStart.setHours(hh, mm, 0, 0);
      const cellStartMs = cellStart.getTime();
      const cellEndMs   = cellStartMs + 15 * 60_000;
      if (cellStartMs < blockEndMs && cellEndMs > blockStartMs) indices.add(idx);
    });
    return indices;
  }

  const tt = tables.map(t => {
    const blockedCellIndices = computeBlockedCellIndices(t.tableId);
    return {
      tableId:   t.tableId,
      seats:     t.seats,
      tableType: t.tableType,
      isBlocked: blockedCellIndices.size > 0,
      blockedCellIndices,
      block:     tableBlocks[String(t.tableId)] ?? null,
      times:     intervals.map(() => null),
    };
  });

  reservations.forEach(r => {
    const row = tt.find(t => String(t.tableId) === String(r.tableId));
    if (!row) return;
    const idx = intervals.findIndex(iv => iv === r.time);
    if (idx === -1) return;
    // Use per-reservation duration if available (customDurationMinutes)
    const resBlockedSlots = r.customDurationMinutes
      ? Math.max(1, Math.round(r.customDurationMinutes / 15))
      : blockedSlots;
    for (let i = idx - (resBlockedSlots - 1); i < idx + resBlockedSlots; i++) {
      if (i >= 0 && i < intervals.length) {
        const isStart = r.time === intervals[i];
        if (!row.times[i] || isStart) {
          row.times[i] = { id: r.id, time: r.time, customerName: r.customerName, partySize: r.partySize ?? null, isStart };
        }
      }
    }
  });

  // ── Overstay extension pass ──────────────────────────────────────────────────
  // Extend blocking for parties that Firestore has flagged as overstaying
  // (overstayMinutes is written by the POS host screen every 5 min).
  const OVERSTAY_ACTIVE = new Set(['seated', 'unseated', 'paid', 'bussing']);
  reservations.forEach(r => {
    if (!r.overstayMinutes || !OVERSTAY_ACTIVE.has(r.status)) return;
    const row = tt.find(t => String(t.tableId) === String(r.tableId));
    if (!row) return;
    const idx = intervals.findIndex(iv => iv === r.time);
    if (idx === -1) return;
    const resBlockedSlots = r.customDurationMinutes
      ? Math.max(1, Math.round(r.customDurationMinutes / 15))
      : blockedSlots;
    const extraSlots  = Math.ceil(r.overstayMinutes / 15);
    const extendedEnd = idx + resBlockedSlots + extraSlots;
    for (let i = idx + resBlockedSlots; i < extendedEnd; i++) {
      if (i >= 0 && i < intervals.length && !row.times[i]) {
        row.times[i] = { id: r.id, time: r.time, customerName: r.customerName, partySize: r.partySize ?? null, isStart: false };
        if (!row.blockedCellIndices) row.blockedCellIndices = new Set();
        row.blockedCellIndices.add(i);
      }
    }
  });

  // ── Buffer pass ──────────────────────────────────────────────────────────────
  // Mark the 1 slot immediately after each reservation's full block as a buffer
  // (15-min industry-standard turnover time). Amber, non-bookable.
  reservations.forEach(r => {
    const row = tt.find(t => String(t.tableId) === String(r.tableId));
    if (!row) return;
    const idx = intervals.findIndex(iv => iv === r.time);
    if (idx === -1) return;
    const resBlockedSlots = r.customDurationMinutes
      ? Math.max(1, Math.round(r.customDurationMinutes / 15))
      : blockedSlots;
    const overstayExtra  = r.overstayMinutes ? Math.ceil(r.overstayMinutes / 15) : 0;
    const firstBufferSlot = idx + resBlockedSlots + overstayExtra;
    if (
      firstBufferSlot >= 0 &&
      firstBufferSlot < intervals.length &&
      !row.times[firstBufferSlot] &&
      !row.blockedCellIndices?.has(firstBufferSlot)
    ) {
      if (!row.bufferCellIndices) row.bufferCellIndices = new Set();
      row.bufferCellIndices.add(firstBufferSlot);
      if (!row.bufferCellMeta) row.bufferCellMeta = {};
      row.bufferCellMeta[firstBufferSlot] = { customerName: r.customerName, time: r.time };
    }
  });

  return tt;
}

function fmt24to12(s) {
  if (!s) return '';
  const [hh, mm] = s.split(':');
  let h = parseInt(hh, 10);
  const suf = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${mm}`;
}

// ─── Reservation Grid Modal ───────────────────────────────────────────────────

const GRID_CELL = 44;
const GRID_LABEL_W = 136;

function ReservationGridModal({ restaurantId, restaurantDoc, onClose }) {
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const [selectedDate, setSelectedDate] = useState(today);
  const [reservations, setReservations] = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [atLeastFilter, setAtLeastFilter] = useState(null);
  const [exactFilter,   setExactFilter]   = useState(null);

  // Load reservations for selected date (real-time)
  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    const start = new Date(selectedDate); start.setHours(0,  0,  0,   0);
    const end   = new Date(selectedDate); end.setHours(23, 59, 59, 999);
    const q = query(
      collection(db, 'restaurants_data', restaurantId, 'reservations'),
      where('date', '>=', Timestamp.fromDate(start)),
      where('date', '<=', Timestamp.fromDate(end)),
    );
    const unsub = onSnapshot(q, snap => {
      setReservations(
        snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => !CLOSED_STATUSES.has(r.status))
      );
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [restaurantId, selectedDate]);

  const hours     = restaurantDoc ? getHoursForDate(selectedDate, restaurantDoc) : null;
  const intervals = hours ? buildTimeIntervals(hours.openTime, hours.closeTime) : [];

  const allTables = restaurantDoc?.tables || [];
  const filteredTables = exactFilter
    ? allTables.filter(t => Number(t.seats) === exactFilter)
    : atLeastFilter
    ? allTables.filter(t => Number(t.seats) >= atLeastFilter)
    : allTables;

  const reservationLength = restaurantDoc?.reservationLength || 60;
  const tableBlocks = restaurantDoc?.tableBlocks || {};
  const timeTable = useMemo(
    () => buildTimeTable(filteredTables, reservations, reservationLength, intervals, tableBlocks, selectedDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(filteredTables), JSON.stringify(reservations), reservationLength, JSON.stringify(intervals), JSON.stringify(tableBlocks), selectedDate.getTime()],
  );

  const todayStr = today.toISOString().split('T')[0];
  const selStr   = selectedDate.toISOString().split('T')[0];

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-center justify-center px-2 py-4 bg-black/80">
        <div
          className="bg-neutral-900 border border-neutral-700 rounded-2xl w-full max-h-[92dvh] flex flex-col overflow-hidden"
          style={{ maxWidth: 900 }}
        >
          {/* Header */}
          <div className="shrink-0 px-4 py-3 border-b border-neutral-700 flex flex-wrap items-center gap-2">

            {/* Date */}
            <input
              type="date"
              value={selStr}
              onChange={e => {
                const d = new Date(e.target.value + 'T00:00:00');
                d.setHours(0, 0, 0, 0);
                setSelectedDate(d);
              }}
              className="bg-neutral-800 border border-neutral-600 text-neutral-200 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#5f799a]"
            />
            <button
              onClick={() => setSelectedDate(today)}
              className={`text-sm font-bold px-3 py-2 rounded-lg transition-colors ${
                selStr === todayStr ? 'bg-green-700 text-white' : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'
              }`}
            >
              Today
            </button>

            <span className="text-neutral-700 hidden sm:block">|</span>

            {/* At least filter */}
            <div className="flex items-center gap-1">
              <span className="text-xs text-neutral-300 mr-0.5">Min</span>
              {[2, 4, 6, 8].map(n => (
                <button
                  key={n}
                  onClick={() => { setAtLeastFilter(atLeastFilter === n ? null : n); setExactFilter(null); }}
                  className={`text-xs font-bold w-8 h-8 rounded-lg transition-colors ${
                    atLeastFilter === n ? 'bg-green-700 text-white' : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'
                  }`}
                >{n}</button>
              ))}
            </div>

            {/* Exact filter */}
            <div className="flex items-center gap-1">
              <span className="text-xs text-neutral-300 mr-0.5">Exact</span>
              {[2, 4, 6, 8].map(n => (
                <button
                  key={n}
                  onClick={() => { setExactFilter(exactFilter === n ? null : n); setAtLeastFilter(null); }}
                  className={`text-xs font-bold w-8 h-8 rounded-lg transition-colors ${
                    exactFilter === n ? 'bg-green-700 text-white' : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'
                  }`}
                >{n}</button>
              ))}
            </div>

            <div className="flex-1" />

            {(atLeastFilter || exactFilter) && (
              <button
                onClick={() => { setAtLeastFilter(null); setExactFilter(null); }}
                className="text-xs px-3 py-2 bg-neutral-700 hover:bg-neutral-600 text-neutral-300 rounded-lg transition-colors"
              >
                Reset
              </button>
            )}
            <button
              onClick={onClose}
              className="text-xs px-3 py-2 bg-red-700 hover:bg-red-600 text-white font-bold rounded-lg transition-colors"
            >
              Close
            </button>
          </div>

          {/* Grid area */}
          <div className="flex-1 min-h-0 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-full gap-3 text-neutral-300">
                <div className="w-6 h-6 border-2 border-[#5f799a] border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Loading…</span>
              </div>
            ) : intervals.length === 0 ? (
              <div className="flex items-center justify-center h-full text-neutral-400 text-sm">
                Restaurant is closed on this day.
              </div>
            ) : (
              <div className="overflow-auto h-full">
                <table style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
                  {/* Sticky time header */}
                  <thead>
                    <tr>
                      <th
                        className="sticky left-0 top-0 z-30 bg-neutral-900"
                        style={{ width: GRID_LABEL_W, minWidth: GRID_LABEL_W, padding: 0 }}
                      />
                      {intervals.map((iv, i) => (
                        <th
                          key={i}
                          className="sticky top-0 z-20 bg-neutral-900 text-neutral-600 font-medium text-center border-b border-neutral-800"
                          style={{ width: GRID_CELL, minWidth: GRID_CELL, height: 28, fontSize: 9, padding: 0, verticalAlign: 'bottom', paddingBottom: 2 }}
                        >
                          {i % 4 === 0 ? fmt24to12(iv) : ''}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {timeTable.map((row, rowIdx) => (
                      <tr key={row.tableId}>
                        {/* Sticky label cell */}
                        <td
                          className={`sticky left-0 z-10 pr-2 pl-3 ${rowIdx % 2 === 0 ? 'bg-neutral-900' : 'bg-neutral-800'}`}
                          style={{
                            width: GRID_LABEL_W, minWidth: GRID_LABEL_W, height: GRID_CELL,
                            borderBottom: `${rowIdx % 2 === 0 ? 4 : 8}px solid ${rowIdx % 2 === 0 ? 'white' : 'black'}`,
                            marginBottom: 8,
                          }}
                        >
                          <div className="leading-tight" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span className="text-xs font-bold" style={{ color: row.isBlocked ? '#c4b5fd' : 'white' }}>
                              {row.tableType && row.tableType !== 'no type' ? `${row.tableType} ` : 'Table '}{row.tableId}
                            </span>
                            {row.isBlocked && (
                              <span style={{ fontSize: 8, fontWeight: 700, color: '#a78bfa', background: '#3b1f6e', borderRadius: 3, padding: '0 3px', lineHeight: '14px' }}>BLKD</span>
                            )}
                          </div>
                          <div className="text-neutral-400" style={{ fontSize: 10 }}>{row.seats} seats</div>
                        </td>

                        {/* Time cells */}
                        {row.times.map((cell, colIdx) => {
                          if (row.blockedCellIndices?.has(colIdx) && !cell) {
                            return (
                              <td
                                key={colIdx}
                                title={row.block?.reason ? `Blocked: ${row.block.reason}` : 'Table blocked'}
                                style={{
                                  width: GRID_CELL, minWidth: GRID_CELL, height: GRID_CELL,
                                  backgroundColor: '#2d1b4e',
                                  borderBottom: `${rowIdx % 2 === 0 ? 4 : 8}px solid ${rowIdx % 2 === 0 ? 'white' : 'black'}`,
                                  borderRight: '1px solid rgba(109,40,217,0.3)',
                                  padding: 0, verticalAlign: 'middle',
                                }}
                              >
                                <div style={{ fontSize: 12, lineHeight: 1, textAlign: 'center', color: '#7c3aed', fontWeight: 900 }}>×</div>
                              </td>
                            );
                          }

                          // 15-min turnover buffer — amber, non-bookable
                          if (!cell && row.bufferCellIndices?.has(colIdx)) {
                            const meta = row.bufferCellMeta?.[colIdx];
                            return (
                              <td
                                key={colIdx}
                                title={meta ? `Buffer after ${meta.customerName}'s reservation` : '15-min turnover buffer'}
                                style={{
                                  width: GRID_CELL, minWidth: GRID_CELL, height: GRID_CELL,
                                  backgroundColor: '#78350f',
                                  borderBottom: `${rowIdx % 2 === 0 ? 4 : 8}px solid ${rowIdx % 2 === 0 ? 'white' : 'black'}`,
                                  borderRight: '1px solid rgba(217,119,6,0.35)',
                                  padding: 0, verticalAlign: 'middle',
                                }}
                              >
                                <div style={{ fontSize: 8, lineHeight: 1.3, textAlign: 'center', padding: '0 1px' }}>
                                  {meta?.customerName
                                    ? <><div style={{ color: '#fde68a', fontWeight: 700 }}>{meta.customerName.split(' ')[0]}</div><div style={{ color: '#d97706' }}>↑ buf</div></>
                                    : <div style={{ color: '#d97706', fontWeight: 700 }}>buf</div>
                                  }
                                </div>
                              </td>
                            );
                          }

                          const isStart = cell?.isStart;
                          const bg = cell ? (isStart ? '#f4b6b6' : '#f8d7da') : '#d4edda';
                          return (
                            <td
                              key={colIdx}
                              style={{
                                width: GRID_CELL, minWidth: GRID_CELL, height: GRID_CELL,
                                backgroundColor: bg,
                                borderBottom: `${rowIdx % 2 === 0 ? 4 : 8}px solid ${rowIdx % 2 === 0 ? 'white' : 'black'}`,
                                borderRight: '1px solid rgba(255,255,255,0.07)',
                                padding: 0, verticalAlign: 'middle',
                              }}
                            >
                              <div style={{ fontSize: 9, lineHeight: 1.3, textAlign: 'center', padding: '0 1px' }}>
                                {cell ? (
                                  isStart ? (
                                    <>
                                      <div style={{ fontWeight: 900, color: '#111' }}>{fmt24to12(cell.time)}</div>
                                      <div style={{ fontWeight: 700, color: '#444' }}>P-{cell.partySize}</div>
                                    </>
                                  ) : (
                                    <>
                                      <div style={{ color: '#666' }}>{fmt24to12(intervals[colIdx])}</div>
                                      <div style={{ color: '#555' }}>{(cell.customerName || '').substring(0, 5)}</div>
                                    </>
                                  )
                                ) : (
                                  <div style={{ color: '#6b7280' }}>{fmt24to12(intervals[colIdx])}</div>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}

// ─── Pickup Orders Panel ─────────────────────────────────────────────────────

function pickupElapsed(ts) {
  if (!ts) return '';
  const mins = Math.floor((Date.now() - ts) / 60_000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}
function pickupOrderNumber() { return 'P' + String(Date.now()).slice(-4); }
const PICKUP_EMPTY_ITEM = { name: '', price: '', note: '' };

function PickupOrderFormModal({ initial, onSave, onClose, saving }) {
  const isEdit = !!initial;
  const [name,  setName]  = useState(initial?.customerName || '');
  const [phone, setPhone] = useState(initial?.customerPhone || '');
  const [notes, setNotes] = useState(initial?.notes || '');
  const [items, setItems] = useState(
    initial?.items?.length
      ? initial.items.map(i => ({ name: i.name, price: String(i.price ?? ''), note: i.note || '' }))
      : [{ ...PICKUP_EMPTY_ITEM }]
  );
  const setItem    = (idx, f, v) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, [f]: v } : it));
  const addItem    = () => setItems(prev => [...prev, { ...PICKUP_EMPTY_ITEM }]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const canSave = name.trim() && items.some(i => i.name.trim());
  const handleSave = () => {
    const parsed = items.filter(i => i.name.trim()).map((i, idx) => ({
      id: `item_${idx}_${Date.now()}`, key: `${idx}_${Date.now()}`,
      name: i.name.trim(), price: parseFloat(i.price) || 0,
      note: i.note.trim(), selectedOptions: [], itemStatus: 'pending',
    }));
    onSave({ name: name.trim(), phone: phone.replace(/\D/g,''), notes: notes.trim(), items: parsed, subtotal: parsed.reduce((s,i) => s+i.price, 0) });
  };
  return (
    <Portal>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center px-4 py-6">
        <div className="bg-white rounded-2xl w-full max-w-md flex flex-col max-h-[90dvh]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0 border-b border-neutral-100">
            <h3 className="text-base font-semibold text-neutral-800">{isEdit ? 'Edit Order' : 'New Pickup Order'}</h3>
            <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">×</button>
          </div>
          <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-neutral-500 mb-1">Customer Name *</label>
              <input className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5f799a]"
                value={name} onChange={e => setName(e.target.value)} placeholder="e.g. John Smith" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-500 mb-1">Phone (optional)</label>
              <input className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5f799a]"
                value={phone} onChange={e => setPhone(e.target.value)} placeholder="10 digits" inputMode="tel" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-neutral-500">Items *</label>
                <button onClick={addItem} className="text-xs text-[#5f799a] font-semibold">+ Add item</button>
              </div>
              <div className="space-y-2">
                {items.map((item, idx) => (
                  <div key={idx} className="border border-neutral-200 rounded-xl p-3 space-y-2">
                    <div className="flex gap-2">
                      <input className="flex-1 border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5f799a]"
                        value={item.name} onChange={e => setItem(idx,'name',e.target.value)} placeholder="Item name" />
                      <input className="w-24 border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5f799a]"
                        value={item.price} onChange={e => setItem(idx,'price',e.target.value)} placeholder="$0.00" inputMode="decimal" />
                      {items.length > 1 && (
                        <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600 text-lg leading-none px-1">×</button>
                      )}
                    </div>
                    <input className="w-full border border-neutral-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#5f799a]"
                      value={item.note} onChange={e => setItem(idx,'note',e.target.value)} placeholder="Special instructions (optional)" />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-500 mb-1">Order Notes (optional)</label>
              <textarea className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5f799a] resize-none"
                rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any special requests…" />
            </div>
          </div>
          <div className="px-5 pb-5 pt-3 shrink-0 border-t border-neutral-100 flex gap-3">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-neutral-200 text-sm font-semibold text-neutral-500 hover:bg-neutral-50">Cancel</button>
            <button onClick={handleSave} disabled={!canSave || saving}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40"
              style={{ background: (canSave && !saving) ? '#5f799a' : '#9ca3af' }}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Order'}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

function PickupPaymentModal({ order, onCollect, onClose }) {
  const [busy, setBusy] = useState(false);
  const handle = async (method) => { setBusy(true); await onCollect(order, method); setBusy(false); };
  return (
    <Portal>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center px-4 py-6">
        <div className="bg-white rounded-2xl w-full max-w-sm flex flex-col max-h-[90dvh]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="px-5 pt-5 pb-3 shrink-0 border-b border-neutral-100">
            <h3 className="text-base font-semibold text-neutral-800">Collect Payment</h3>
            <p className="text-sm text-neutral-500 mt-0.5">
              {order.customerName} · <span className="font-semibold text-neutral-700">${(order.total ?? order.subtotal ?? 0).toFixed(2)}</span>
            </p>
          </div>
          <div className="px-5 py-4 space-y-3">
            {['Cash', 'Card', 'Other'].map(method => (
              <button key={method} onClick={() => handle(method)} disabled={busy}
                className="w-full py-3 rounded-xl border-2 text-sm font-semibold transition-colors hover:border-[#5f799a] hover:text-[#5f799a] border-neutral-200 text-neutral-700 disabled:opacity-50">
                {method === 'Cash' ? '💵 Cash' : method === 'Card' ? '💳 Card' : '🧾 Other'}
              </button>
            ))}
          </div>
          <div className="px-5 pb-5 shrink-0">
            <button onClick={onClose} className="w-full py-2.5 rounded-xl border border-neutral-200 text-sm font-semibold text-neutral-500 hover:bg-neutral-50">Cancel</button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

const PICKUP_STATUS_GROUPS = [
  { key: 'new',      dot: 'bg-red-500'   },
  { key: 'accepted', dot: 'bg-amber-500' },
  { key: 'ready',    dot: 'bg-green-500' },
];

function PickupOrdersPanel({ restaurantId }) {
  const [orders,      setOrders]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [expanded,    setExpanded]    = useState({});
  const [payModal,    setPayModal]    = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [editModal,   setEditModal]   = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [busyId,      setBusyId]      = useState(null);
  const [decliningId, setDecliningId] = useState(null);

  useEffect(() => {
    if (!restaurantId) return;
    const q = query(
      collection(db, 'restaurants_data', restaurantId, 'pickup_orders'),
      where('status', 'not-in', ['picked_up', 'cancelled'])
    );
    const unsub = onSnapshot(q, snap => {
      setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.createdAt||0) - (b.createdAt||0)));
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [restaurantId]);

  const grouped = {
    new:      orders.filter(o => o.status === 'new'),
    accepted: orders.filter(o => o.status === 'accepted'),
    ready:    orders.filter(o => o.status === 'ready'),
  };

  const withBusy = async (id, fn) => { setBusyId(id); try { await fn(); } finally { setBusyId(null); } };
  const ref = (orderId) => doc(db, 'restaurants_data', restaurantId, 'pickup_orders', orderId);
  const acceptOrder  = (o) => withBusy(o.id, () => updateDoc(ref(o.id), { status: 'accepted', acceptedAt: Date.now() }));
  const markReady    = (o) => withBusy(o.id, () => updateDoc(ref(o.id), { status: 'ready',    readyAt:    Date.now() }));
  const markPickedUp = (o) => withBusy(o.id, () => updateDoc(ref(o.id), { status: 'picked_up', pickedUpAt: Date.now() }));

  const declineOrder = async (o) => {
    setDecliningId(o.id);
    try { await _declinePickupOrder({ restaurantId, orderId: o.id }); }
    catch { await updateDoc(ref(o.id), { status: 'cancelled', cancelledAt: Date.now() }); }
    finally { setDecliningId(null); }
  };

  const collectPayment = async (o, method) => {
    await updateDoc(ref(o.id), { paymentStatus: 'paid', payment: { methodLabel: method } });
    setPayModal(null);
  };

  const createOrder = async ({ name, phone, notes, items, subtotal }) => {
    setSaving(true);
    try {
      await addDoc(collection(db, 'restaurants_data', restaurantId, 'pickup_orders'), {
        orderNumber: pickupOrderNumber(), source: 'host', customerName: name,
        customerPhone: phone || null, items, subtotal, tax: 0, total: subtotal,
        notes: notes || '', paymentStatus: 'pending', status: 'new', createdAt: Date.now(),
      });
      setCreateModal(false);
    } finally { setSaving(false); }
  };

  const saveEdit = async ({ name, phone, notes, items, subtotal }) => {
    if (!editModal) return;
    setSaving(true);
    try {
      await updateDoc(ref(editModal.id), { customerName: name, customerPhone: phone || null, items, subtotal, total: subtotal, notes: notes || '' });
      setEditModal(null);
    } finally { setSaving(false); }
  };

  return (
    <>
      <div className="w-full lg:rounded-xl border-y lg:border border-neutral-700 bg-neutral-900 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="shrink-0 px-4 py-3 border-b border-neutral-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-neutral-200">Pickup Orders</p>
            {orders.length > 0 && (
              <span className="text-[10px] font-bold bg-neutral-700 text-neutral-300 px-1.5 py-0.5 rounded-full">{orders.length}</span>
            )}
            {grouped.new.length > 0 && (
              <span className="text-[10px] font-bold bg-red-900/60 text-red-300 px-1.5 py-0.5 rounded-full">{grouped.new.length} new</span>
            )}
          </div>
          <button
            onClick={() => setCreateModal(true)}
            className="text-[10px] font-semibold px-2.5 py-1 rounded-lg border border-neutral-600 text-neutral-400 hover:text-neutral-200 hover:border-neutral-400 transition-colors"
          >
            + New Order
          </button>
        </div>

        {/* Order list */}
        <div className="overflow-y-auto divide-y divide-neutral-800" style={{ maxHeight: 320 }}>
          {loading && <div className="flex items-center justify-center py-8 text-neutral-400 text-xs">Loading…</div>}
          {!loading && orders.length === 0 && (
            <div className="flex items-center justify-center py-8 text-neutral-400 text-xs">No active pickup orders</div>
          )}
          {!loading && PICKUP_STATUS_GROUPS.map(group =>
            grouped[group.key].map(o => {
              const isExp       = !!expanded[o.id];
              const isBusy      = busyId === o.id;
              const isDeclining = decliningId === o.id;
              const since       = pickupElapsed(o.createdAt);
              return (
                <div key={o.id}>
                  <button
                    onClick={() => setExpanded(prev => ({ ...prev, [o.id]: !prev[o.id] }))}
                    className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-neutral-800/60 transition-colors"
                  >
                    <span className={`shrink-0 w-2 h-2 rounded-full ${group.dot}`} />
                    <span className="shrink-0 text-xs font-bold text-neutral-300 font-mono">{o.orderNumber || o.id?.slice(-4)}</span>
                    <span className="flex-1 text-xs text-neutral-200 truncate min-w-0">{o.customerName}</span>
                    <span className="shrink-0 text-[10px] text-neutral-500">{o.items?.length || 0}×</span>
                    <span className="shrink-0 text-[10px] font-semibold text-neutral-300">${(o.total ?? o.subtotal ?? 0).toFixed(2)}</span>
                    {since && <span className="shrink-0 text-[10px] text-neutral-500">{since}</span>}
                    <span className="shrink-0 text-neutral-500 text-[10px]">{isExp ? '▲' : '▼'}</span>
                  </button>

                  {isExp && (
                    <div className="bg-neutral-800/40 border-t border-neutral-800 px-3 py-3 space-y-2">
                      <div className="space-y-0.5">
                        {(o.items || []).map((item, idx) => (
                          <div key={item.key || idx} className="flex items-start justify-between gap-2 text-xs text-neutral-300">
                            <span className="truncate">• {item.name}{item.note ? ` — ${item.note}` : ''}</span>
                            <span className="shrink-0 text-neutral-400">${(item.price || 0).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      {o.notes && <p className="text-[10px] text-neutral-400 italic">{o.notes}</p>}
                      <div>
                        {o.paymentStatus === 'paid'
                          ? <span className="text-[10px] font-semibold text-green-400">✓ Paid · {o.payment?.methodLabel || ''}</span>
                          : <span className="text-[10px] text-amber-400">Payment pending</span>
                        }
                      </div>
                      <div className="flex flex-wrap gap-2 pt-1">
                        {o.status === 'new' && (<>
                          <button onClick={() => acceptOrder(o)} disabled={isBusy}
                            className="flex-1 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                            style={{ backgroundColor: '#5f799a' }}>
                            {isBusy ? '…' : 'Accept'}
                          </button>
                          <button onClick={() => declineOrder(o)} disabled={isDeclining}
                            className="flex-1 py-1.5 rounded-lg text-xs font-semibold border border-red-700 text-red-400 hover:bg-red-900/30 disabled:opacity-50">
                            {isDeclining ? '…' : 'Decline'}
                          </button>
                        </>)}
                        {o.status === 'accepted' && (
                          <button onClick={() => markReady(o)} disabled={isBusy}
                            className="flex-1 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                            style={{ backgroundColor: '#C9A84C' }}>
                            {isBusy ? '…' : '✓ Mark Ready'}
                          </button>
                        )}
                        {o.status === 'ready' && (<>
                          {o.paymentStatus !== 'paid' && (
                            <button onClick={() => setPayModal(o)}
                              className="flex-1 py-1.5 rounded-lg text-xs font-semibold text-white"
                              style={{ backgroundColor: '#C9A84C' }}>
                              💳 Collect Pay
                            </button>
                          )}
                          <button onClick={() => markPickedUp(o)} disabled={isBusy}
                            className="flex-1 py-1.5 rounded-lg text-xs font-semibold bg-neutral-700 text-white hover:bg-neutral-600 disabled:opacity-50">
                            {isBusy ? '…' : '✓ Picked Up'}
                          </button>
                        </>)}
                        <button onClick={() => setEditModal(o)}
                          className="px-2 py-1.5 rounded-lg text-xs text-neutral-400 hover:text-neutral-200 hover:bg-neutral-700 transition-colors">
                          Edit
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {createModal && <PickupOrderFormModal onSave={createOrder} onClose={() => setCreateModal(false)} saving={saving} />}
      {editModal   && <PickupOrderFormModal initial={editModal} onSave={saveEdit} onClose={() => setEditModal(null)} saving={saving} />}
      {payModal    && <PickupPaymentModal order={payModal} onCollect={collectPayment} onClose={() => setPayModal(null)} />}
    </>
  );
}

// ─── Upcoming reservations panel ─────────────────────────────────────────────

function UpcomingPanel({ rawReservations, reservationLength, now, restaurantDoc, restaurantId }) {
  const [gridOpen, setGridOpen] = useState(false);

  const upcoming = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return rawReservations
      .filter(r => {
        if (CLOSED_STATUSES.has(r.status)) return false;
        if (r.isBarSeat) return false; // bar tabs are walk-up, not scheduled
        const rDate = r.date?.toDate?.();
        if (!rDate) return false;
        const rDay = new Date(rDate); rDay.setHours(0, 0, 0, 0);
        if (rDay.getTime() !== today.getTime()) return false;
        const startMs = parseStartMs(rDate, r.time);
        return startMs + reservationLength * 60_000 > now - 60_000;
      })
      .map(r => ({ ...r, startMs: parseStartMs(r.date.toDate(), r.time) }))
      .sort((a, b) => a.startMs - b.startMs);
  }, [rawReservations, reservationLength, now]);

  return (
    <>
      <div className="w-full lg:rounded-xl border-y lg:border border-neutral-700 bg-neutral-900 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="shrink-0 px-4 py-3 border-b border-neutral-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-neutral-200">Today's Reservations</p>
            {upcoming.length > 0 && (
              <span className="text-[10px] font-bold bg-neutral-700 text-neutral-300 px-1.5 py-0.5 rounded-full">
                {upcoming.length}
              </span>
            )}
          </div>
          <button
            onClick={() => setGridOpen(true)}
            className="text-[10px] font-semibold px-2.5 py-1 rounded-lg border border-neutral-600 text-neutral-400 hover:text-neutral-200 hover:border-neutral-400 transition-colors"
          >
            ↗ Expand
          </button>
        </div>

        {/* List */}
        <div className="overflow-y-auto divide-y divide-neutral-800" style={{ maxHeight: 260 }}>
          {upcoming.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-neutral-400 text-xs">
              No reservations today
            </div>
          ) : (
            upcoming.map(r => {
              const endMs     = r.startMs + reservationLength * 60_000;
              const isActive  = r.startMs <= now && now < endMs;
              const isDone    = endMs <= now;

              const statusLabel = isActive ? (r.status === 'seated' ? 'Seated' : 'Active') : isDone ? 'Done' : 'Upcoming';
              const statusCls   = isActive ? 'text-red-400' : isDone ? 'text-neutral-400' : 'text-[#7fa3cc]';

              return (
                <div
                  key={r.id}
                  className={`px-3 py-2 flex items-center gap-2 ${isDone ? 'opacity-40' : ''}`}
                >
                  <span className="shrink-0 text-xs font-bold text-neutral-200 font-mono w-14 text-right">
                    {fmt12(r.time)}
                  </span>
                  <span className="shrink-0 text-[10px] text-neutral-500 bg-neutral-800 border border-neutral-700 px-1.5 py-0.5 rounded">
                    T{r.tableId}
                  </span>
                  <span className="flex-1 text-xs text-neutral-200 truncate min-w-0">
                    {r.customerName}
                  </span>
                  <span className="shrink-0 text-[10px] text-neutral-300">{r.partySize}p</span>
                  <span className={`shrink-0 text-[10px] font-semibold ${statusCls}`}>{statusLabel}</span>
                </div>
              );
            })
          )}
        </div>
      </div>

      {gridOpen && (
        <ReservationGridModal
          restaurantId={restaurantId}
          restaurantDoc={restaurantDoc}
          onClose={() => setGridOpen(false)}
        />
      )}
    </>
  );
}

// ─── Confirmations Panel ─────────────────────────────────────────────────────

// For the confirmations panel only: statuses that are fully hidden (canceled is shown separately)
const CONF_FULLY_CLOSED = new Set(['comped', 'voided', 'walk_out', 'fullyPaid', 'completed', 'noShow']);
const CONF_CANCELED = new Set(['cancelled', 'canceled']);

function ConfirmationsPanel({ rawReservations, restaurantId, now }) {
  const [confirming, setConfirming] = useState(null); // reservation id being confirmed

  // Today's reservations that aren't fully closed — walk-ins excluded
  const todayAll = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return rawReservations
      .filter(r => {
        if (CONF_FULLY_CLOSED.has(r.status)) return false;
        if (r.isBarSeat) return false;
        if (r.isWalkIn) return false;
        const rDate = r.date?.toDate?.();
        if (!rDate) return false;
        const rDay = new Date(rDate); rDay.setHours(0, 0, 0, 0);
        return rDay.getTime() === today.getTime();
      })
      .map(r => ({ ...r, startMs: parseStartMs(r.date.toDate(), r.time) }))
      .sort((a, b) => a.startMs - b.startMs);
  }, [rawReservations]);

  const canceled    = todayAll.filter(r => CONF_CANCELED.has(r.status));
  const todayPhone  = todayAll.filter(r => !CONF_CANCELED.has(r.status));
  const confirmed   = todayPhone.filter(r => r.sms_confirmationStatus === 'confirmed' || r.sms_confirmationStatus === 'confirmed_host');
  const unconfirmed = todayPhone.filter(r => r.sms_confirmationStatus !== 'confirmed' && r.sms_confirmationStatus !== 'confirmed_host');

  const handleManualConfirm = async (r) => {
    setConfirming(r.id);
    try {
      await updateDoc(
        doc(db, 'restaurants_data', restaurantId, 'reservations', r.id),
        { sms_confirmationStatus: 'confirmed_host' }
      );
    } catch (_) {
    } finally {
      setConfirming(null);
    }
  };

  const statusBadge = (r) => {
    if (CONF_CANCELED.has(r.status)) {
      return <span className="shrink-0 text-[10px] font-semibold text-red-400">Canceled</span>;
    }
    const s = r.sms_confirmationStatus;
    if (s === 'confirmed' || s === 'confirmed_host') {
      return <span className="shrink-0 text-[10px] font-semibold text-green-400">Confirmed</span>;
    }
    if (s === 'sent') {
      return <span className="shrink-0 text-[10px] font-semibold text-amber-400">Awaiting</span>;
    }
    if (s === 'declined') {
      return <span className="shrink-0 text-[10px] font-semibold text-red-400">Canceled</span>;
    }
    if (s === 'no_phone') {
      return <span className="shrink-0 text-[10px] text-neutral-500">No phone</span>;
    }
    return <span className="shrink-0 text-[10px] text-neutral-500">Not sent</span>;
  };

  const renderRow = (r, showConfirmBtn) => (
    <div key={r.id} className="px-3 py-2 flex items-center gap-2">
      <span className="shrink-0 text-xs font-bold text-neutral-200 font-mono w-14 text-right">
        {fmt12(r.time)}
      </span>
      <span className="flex-1 text-xs text-neutral-200 truncate min-w-0">{r.customerName}</span>
      <span className="shrink-0 text-[10px] text-neutral-300">{r.partySize}p</span>
      {statusBadge(r)}
      {showConfirmBtn && !CONF_CANCELED.has(r.status) && r.sms_confirmationStatus !== 'confirmed' && r.sms_confirmationStatus !== 'confirmed_host' && (
        <button
          disabled={confirming === r.id}
          onClick={() => handleManualConfirm(r)}
          title="Mark as confirmed"
          className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-neutral-800 border border-neutral-600 text-neutral-400 hover:bg-green-700 hover:border-green-500 hover:text-white transition-colors disabled:opacity-40"
        >
          {confirming === r.id ? (
            <span className="block w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          )}
        </button>
      )}
    </div>
  );

  return (
    <div className="w-full lg:rounded-xl border-y lg:border border-neutral-700 bg-neutral-900 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="shrink-0 px-4 py-3 border-b border-neutral-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-neutral-200">Confirmations</p>
          {unconfirmed.length > 0 && (
            <span className="text-[10px] font-bold bg-amber-600 text-white px-1.5 py-0.5 rounded-full">
              {unconfirmed.length} unconfirmed
            </span>
          )}
          {canceled.length > 0 && (
            <span className="text-[10px] font-bold bg-red-700 text-white px-1.5 py-0.5 rounded-full">
              {canceled.length} canceled
            </span>
          )}
        </div>
        {confirmed.length > 0 && (
          <span className="text-[10px] text-green-400 font-semibold">{confirmed.length} confirmed</span>
        )}
      </div>

      {/* Unconfirmed list */}
      {unconfirmed.length > 0 && (
        <div className="overflow-y-auto divide-y divide-neutral-800" style={{ maxHeight: 200 }}>
          {unconfirmed.map(r => renderRow(r, true))}
        </div>
      )}

      {/* Confirmed list */}
      {confirmed.length > 0 && (
        <>
          <div className="px-4 py-1.5 bg-neutral-800/50 border-t border-neutral-700">
            <p className="text-[10px] text-neutral-500 font-semibold uppercase tracking-wide">Confirmed</p>
          </div>
          <div className="overflow-y-auto divide-y divide-neutral-800" style={{ maxHeight: 140 }}>
            {confirmed.map(r => renderRow(r, false))}
          </div>
        </>
      )}

      {/* Canceled list */}
      {canceled.length > 0 && (
        <>
          <div className="px-4 py-1.5 bg-red-950/40 border-t border-neutral-700">
            <p className="text-[10px] text-red-400 font-semibold uppercase tracking-wide">Canceled</p>
          </div>
          <div className="overflow-y-auto divide-y divide-neutral-800" style={{ maxHeight: 140 }}>
            {canceled.map(r => renderRow(r, false))}
          </div>
        </>
      )}

      {todayAll.length === 0 && (
        <div className="flex items-center justify-center py-8 text-neutral-400 text-xs">
          No reservations today
        </div>
      )}
    </div>
  );
}

// ─── Waitlist helpers ─────────────────────────────────────────────────────────

function elapsedStr(createdAt, now) {
  const totalMin = Math.floor(Math.max(0, now - (createdAt || now)) / 60_000);
  if (totalMin < 60) return `${totalMin}m`;
  return `${Math.floor(totalMin / 60)}h ${totalMin % 60}m`;
}

function waitColorClass(createdAt, estimatedWait, now) {
  if (!createdAt || !estimatedWait) return 'text-neutral-400';
  const elapsedMin = (now - createdAt) / 60_000;
  if (elapsedMin >= estimatedWait)            return 'text-red-400';
  if (elapsedMin >= estimatedWait * 0.75)     return 'text-amber-400';
  return 'text-green-400';
}

// ─── Add Guest Modal ──────────────────────────────────────────────────────────

function AddGuestModal({ restaurantId, tableTypes, onClose }) {
  const [form, setForm] = useState({
    name: '', partySize: '2', phone: '', tableType: '', estimatedWait: '20',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const phoneDigits = form.phone.replace(/\D/g, '');
    if (!form.name.trim() || phoneDigits.length !== 10) return;
    setSaving(true);
    setError('');
    try {
      await addDoc(collection(db, 'restaurants_data', restaurantId, 'waitlist'), {
        name:          form.name.trim(),
        partySize:     parseInt(form.partySize, 10) || 2,
        phone:         form.phone.trim(),
        tableType:     form.tableType,
        estimatedWait: parseInt(form.estimatedWait, 10) || 20,
        createdAt:     Date.now(),
        tableReady:    false,
        sms_tableReady: false,
        preAssigned:   false,
      });
      onClose();
    } catch {
      setError('Failed to add guest. Please try again.');
      setSaving(false);
    }
  };

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 py-6 bg-black/70">
        <div className="bg-neutral-900 border border-neutral-700 rounded-2xl w-full max-w-sm max-h-[90dvh] flex flex-col" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>

          {/* Header */}
          <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-neutral-700">
            <p className="text-sm font-semibold text-neutral-200">Add to Waitlist</p>
            <button
              onClick={onClose}
              className="text-neutral-400 hover:text-white text-2xl leading-none transition-colors"
            >×</button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">

            <div>
              <label className="block text-sm text-neutral-300 mb-1">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => set('name', e.target.value)}
                placeholder="Guest name"
                className="w-full bg-neutral-800 border border-neutral-700 text-neutral-200 text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#5f799a] placeholder-neutral-600"
                disabled={saving}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-neutral-300 mb-1">Party Size</label>
                <input
                  type="number" min="1" max="30"
                  value={form.partySize}
                  onChange={e => set('partySize', e.target.value)}
                  className="w-full bg-neutral-800 border border-neutral-700 text-neutral-200 text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#5f799a]"
                  disabled={saving}
                />
              </div>
              <div>
                <label className="block text-sm text-neutral-300 mb-1">Est. Wait (min)</label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {['5','10','15','20','25','30','35','40','45','50','60','75','90'].map(val => (
                    <button
                      key={val}
                      type="button"
                      disabled={saving}
                      onClick={() => set('estimatedWait', val)}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        form.estimatedWait === val
                          ? 'bg-[#5f799a] border-[#5f799a] text-white font-bold'
                          : 'bg-neutral-800 border-neutral-600 text-neutral-300 hover:border-[#5f799a] hover:text-white'
                      }`}
                    >
                      {val}m
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm text-neutral-300 mb-1">Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={e => set('phone', formatPhone(e.target.value))}
                placeholder="555-867-5309"
                maxLength={12}
                className="w-full bg-neutral-800 border border-neutral-700 text-neutral-200 text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#5f799a] placeholder-neutral-600"
                disabled={saving}
              />
            </div>

            {tableTypes.length > 0 && (
              <div>
                <label className="block text-sm text-neutral-300 mb-1">Table Preference</label>
                <select
                  value={form.tableType}
                  onChange={e => set('tableType', e.target.value)}
                  className="w-full bg-neutral-800 border border-neutral-700 text-neutral-200 text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#5f799a]"
                  disabled={saving}
                >
                  <option value="">Any</option>
                  {tableTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            )}

            {error && (
              <p className="text-sm text-red-400 bg-red-900/20 border border-red-800/30 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            {/* Actions */}
            <div className="shrink-0 flex gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="flex-1 py-2.5 text-sm font-medium rounded-xl border border-neutral-700 text-neutral-400 hover:text-neutral-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !form.name.trim() || form.phone.replace(/\D/g, '').length !== 10}
                className="flex-1 py-2.5 text-sm font-semibold rounded-xl bg-[#5f799a] hover:bg-[#4d6485] text-white disabled:opacity-50 transition-colors"
              >
                {saving ? 'Adding…' : 'Add Guest'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Portal>
  );
}

// ─── Waitlist Panel ───────────────────────────────────────────────────────────

function WaitlistPanel({ restaurantId, tables, reservations, rawReservations, reservationLength, tableBlocks, now }) {
  const [entries,        setEntries]        = useState([]);
  const [addOpen,        setAddOpen]        = useState(false);
  const [notifying,      setNotifying]      = useState({});
  const [confirmDelete,  setConfirmDelete]  = useState(null); // entry pending delete
  const [etaEdits,       setEtaEdits]       = useState({}); // { [entryId]: number }
  const [savingEta,      setSavingEta]      = useState({}); // { [entryId]: bool }
  const [holdOpen,       setHoldOpen]       = useState(null); // entry to hold a table for

  // Unique table types from the configured tables (for the "Add" form)
  const tableTypes = useMemo(() => {
    const types = [...new Set(tables.map(t => t.tableType).filter(Boolean))];
    return types;
  }, [tables]);

  useEffect(() => {
    if (!restaurantId) return;
    const unsub = onSnapshot(
      collection(db, 'restaurants_data', restaurantId, 'waitlist'),
      snap => {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
        setEntries(docs);
      },
      () => {},
    );
    return unsub;
  }, [restaurantId]);

  const handleNotify = async (entry) => {
    if (notifying[entry.id] || entry.sms_tableReady || entry.tableReady) return;
    setNotifying(prev => ({ ...prev, [entry.id]: true }));
    try {
      await updateDoc(
        doc(db, 'restaurants_data', restaurantId, 'waitlist', entry.id),
        { tableReady: true },
      );
    } catch (e) {
      console.error('Waitlist notify failed:', e);
    } finally {
      setNotifying(prev => { const n = { ...prev }; delete n[entry.id]; return n; });
    }
  };

  const handleDelete = async (entry) => {
    try {
      await deleteDoc(doc(db, 'restaurants_data', restaurantId, 'waitlist', entry.id));
    } catch (e) {
      console.error('Waitlist delete failed:', e);
    } finally {
      setConfirmDelete(null);
    }
  };

  const getEtaValue = (entry) =>
    etaEdits[entry.id] !== undefined ? etaEdits[entry.id] : (entry.estimatedWait ?? 15);

  const adjustEta = (entry, delta) => {
    setEtaEdits(prev => ({
      ...prev,
      [entry.id]: Math.max(1, getEtaValue(entry) + delta),
    }));
  };

  const handleSaveEta = async (entry, notify) => {
    const newEta = getEtaValue(entry);
    setSavingEta(prev => ({ ...prev, [entry.id]: true }));
    try {
      await _updateWaitlistEta({ restaurantId, waitlistId: entry.id, estimatedWait: newEta, notify });
      setEtaEdits(prev => { const n = { ...prev }; delete n[entry.id]; return n; });
    } catch (e) {
      console.error('ETA update failed:', e);
    } finally {
      setSavingEta(prev => { const n = { ...prev }; delete n[entry.id]; return n; });
    }
  };

  const handleHoldTable = async (entry, tableId) => {
    // If tapping the already-held table, clear the hold
    const newValue = String(entry.preAssigned) === String(tableId) ? deleteField() : String(tableId);
    try {
      await updateDoc(
        doc(db, 'restaurants_data', restaurantId, 'waitlist', entry.id),
        { preAssigned: newValue },
      );
    } catch (e) {
      console.error('Hold table failed:', e);
    } finally {
      setHoldOpen(null);
    }
  };

  // Next-available time grouped by slot — accounts for reservations, customDurationMinutes, and tableBlocks
  const availabilityGroups = useMemo(
    () => getTableAvailabilityGroups(tables, rawReservations, tableBlocks, now, reservationLength),
    [tables, rawReservations, tableBlocks, now, reservationLength],
  );

  return (
    <>
      <div
        className="w-full lg:rounded-xl border-y lg:border border-neutral-700 bg-neutral-900 overflow-hidden flex flex-col"
        style={{ minHeight: 220, maxHeight: '65vh' }}
      >
        {/* Header */}
        <div className="shrink-0 px-4 py-3 border-b border-neutral-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-neutral-200">Waitlist</p>
            {entries.length > 0 && (
              <span className="text-[10px] font-bold bg-neutral-700 text-neutral-300 px-1.5 py-0.5 rounded-full">
                {entries.length}
              </span>
            )}
          </div>
          <button
            onClick={() => setAddOpen(true)}
            className="text-sm px-2.5 py-1 rounded-lg bg-green-700 hover:bg-green-600 text-white font-semibold transition-colors"
          >
            + Add
          </button>
        </div>

        {/* Guest list */}
        <div className="flex-1 overflow-y-auto divide-y divide-neutral-700">
          {entries.length === 0 ? (
            <div className="flex items-center justify-center py-10 text-neutral-400 text-sm">
              No one waiting
            </div>
          ) : (
            entries.map(entry => {
              const isNotified = entry.sms_tableReady || entry.tableReady;
              const wColorCls  = waitColorClass(entry.createdAt, entry.estimatedWait, now);

              const etaVal   = getEtaValue(entry);
              const etaDirty = etaEdits[entry.id] !== undefined && etaEdits[entry.id] !== (entry.estimatedWait ?? 15);
              const isSaving = savingEta[entry.id];

              return (
                <div key={entry.id} className="px-3 py-2.5 flex flex-col gap-1.5">

                  {/* Row 1: name, party size, hold badge, delete */}
                  <div className="flex items-center gap-1.5">
                    <span className="flex-1 text-sm font-semibold text-white truncate min-w-0">
                      {entry.name}
                    </span>
                    {entry.preAssigned && (
                      <span className="shrink-0 text-[10px] font-bold bg-amber-700/50 text-amber-300 border border-amber-600/50 px-1.5 py-0.5 rounded">
                        T{entry.preAssigned}
                      </span>
                    )}
                    <span className="shrink-0 text-sm text-neutral-300">
                      {entry.partySize}p
                    </span>
                    <button
                      onClick={() => setConfirmDelete(entry)}
                      className="shrink-0 text-xs font-medium px-2 py-1 rounded-lg border border-red-700/60 text-red-400 hover:bg-red-900/40 transition-colors ml-1"
                    >
                      Del
                    </button>
                  </div>

                  {/* Row 2: table type, elapsed, notify, hold */}
                  <div className="flex items-center gap-2">
                    {entry.tableType && (
                      <span className="text-xs text-neutral-300 bg-neutral-700 border border-neutral-600 px-1.5 py-0.5 rounded shrink-0">
                        {entry.tableType}
                      </span>
                    )}
                    <span className={`text-sm font-medium flex-1 ${wColorCls}`}>
                      {elapsedStr(entry.createdAt, now)}
                    </span>
                    <button
                      onClick={() => setHoldOpen(entry)}
                      className="shrink-0 text-xs font-semibold px-2 py-1 rounded-lg bg-amber-700/40 hover:bg-amber-700/70 border border-amber-600/50 text-amber-300 transition-colors"
                      title="Pre-assign to a table"
                    >
                      Hold
                    </button>
                    <button
                      onClick={() => handleNotify(entry)}
                      disabled={isNotified || notifying[entry.id]}
                      className={`shrink-0 text-xs font-semibold px-2 py-1 rounded-lg transition-colors ${
                        isNotified
                          ? 'bg-green-900/50 text-green-300 cursor-default'
                          : notifying[entry.id]
                          ? 'bg-neutral-700 text-neutral-400 cursor-wait'
                          : 'bg-[#5f799a] hover:bg-[#4d6485] text-white'
                      }`}
                      title={isNotified ? 'Table ready SMS sent' : 'Send table ready SMS'}
                    >
                      {isNotified ? '✓ Ready' : notifying[entry.id] ? '…' : 'Ready'}
                    </button>
                  </div>

                  {/* Row 3: ETA stepper */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-neutral-400 shrink-0">ETA</span>
                    <button
                      onClick={() => adjustEta(entry, -5)}
                      className="text-xs font-bold w-6 h-6 rounded bg-neutral-700 hover:bg-neutral-600 text-white transition-colors"
                    >−</button>
                    <span className={`text-xs font-bold min-w-[28px] text-center ${etaDirty ? 'text-amber-400' : 'text-neutral-200'}`}>
                      {etaVal}m
                    </span>
                    <button
                      onClick={() => adjustEta(entry, 5)}
                      className="text-xs font-bold w-6 h-6 rounded bg-neutral-700 hover:bg-neutral-600 text-white transition-colors"
                    >+</button>
                    {etaDirty && (
                      <>
                        <button
                          onClick={() => handleSaveEta(entry, false)}
                          disabled={isSaving}
                          className="text-xs font-semibold px-2 py-0.5 rounded bg-[#5f799a] hover:bg-[#4d6485] text-white transition-colors disabled:opacity-50"
                        >
                          {isSaving ? '…' : 'Save'}
                        </button>
                        {entry.phone && (
                          <button
                            onClick={() => handleSaveEta(entry, true)}
                            disabled={isSaving}
                            className="text-xs font-semibold px-2 py-0.5 rounded bg-green-700 hover:bg-green-600 text-white transition-colors disabled:opacity-50"
                            title="Save and SMS guest"
                          >
                            + SMS
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  {/* Row 4: phone (if present) */}
                  {entry.phone ? (
                    <p className="text-xs text-neutral-300 leading-tight">{entry.phone}</p>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Delete confirmation */}
      {confirmDelete && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/70">
            <div className="bg-neutral-900 border border-neutral-700 rounded-2xl w-full max-w-xs p-5 flex flex-col gap-4">
              <p className="text-sm font-semibold text-white">Remove from waitlist?</p>
              <p className="text-sm text-neutral-300">
                Remove <span className="font-semibold text-white">{confirmDelete.name}</span> (party of {confirmDelete.partySize}) from the waitlist?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="flex-1 py-2 text-sm font-medium rounded-xl border border-neutral-600 text-neutral-300 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(confirmDelete)}
                  className="flex-1 py-2 text-sm font-semibold rounded-xl bg-red-700 hover:bg-red-600 text-white transition-colors"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Hold Table picker */}
      {holdOpen && (
        <Portal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-black/70"
            onClick={() => setHoldOpen(null)}
          >
            <div
              className="bg-neutral-900 border border-neutral-700 rounded-2xl w-full max-w-sm flex flex-col max-h-[85dvh]"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="shrink-0 px-5 pt-5 pb-4 border-b border-neutral-700">
                <p className="text-base font-semibold text-white">Hold Table</p>
                <p className="text-xs text-neutral-400 mt-0.5">
                  {holdOpen.name} · {holdOpen.partySize ? `Party of ${holdOpen.partySize}` : 'Guest'}
                </p>
                {holdOpen.preAssigned && (
                  <span className="inline-flex mt-2 text-[10px] font-bold bg-amber-900/50 text-amber-300 border border-amber-700/50 px-2 py-0.5 rounded">
                    Currently holding T{holdOpen.preAssigned}
                  </span>
                )}
              </div>

              {/* Table list — grouped by next available slot */}
              <div className="flex-1 overflow-y-auto px-3 py-2">
                {availabilityGroups.map(group => (
                  <div key={group.label}>
                    {/* Slot header */}
                    <div className="flex items-center gap-1.5 mt-3 mb-1 px-1">
                      <span
                        className="inline-block w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: group.ms === 0 ? '#22c55e' : '#f59e0b' }}
                      />
                      <span
                        className="text-[10px] font-bold uppercase tracking-wide"
                        style={{ color: group.ms === 0 ? '#4ade80' : '#fbbf24' }}
                      >
                        {group.label}
                      </span>
                    </div>
                    {/* Tables in this slot */}
                    {group.tables.map(t => {
                      const isHeld = String(holdOpen.preAssigned) === String(t.tableId);
                      return (
                        <button
                          key={t.tableId}
                          onClick={() => handleHoldTable(holdOpen, t.tableId)}
                          className={`w-full mb-1.5 py-2.5 px-3 rounded-xl text-sm font-medium text-left transition-colors flex items-center gap-3 ${
                            isHeld
                              ? 'bg-amber-900/40 border border-amber-600/60 text-white'
                              : 'bg-neutral-800 border border-neutral-700 text-white hover:bg-neutral-700'
                          }`}
                        >
                          {/* Table badge */}
                          <span className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold ${
                            isHeld ? 'bg-amber-700' : group.ms === 0 ? 'bg-[#1e4d2b]' : 'bg-neutral-700'
                          }`}>
                            T{t.tableId}
                          </span>
                          {/* Info */}
                          <span className="flex flex-col min-w-0 flex-1">
                            {t.tableType && (
                              <span className="text-sm font-semibold capitalize truncate">{t.tableType}</span>
                            )}
                            {t.seats && (
                              <span className="text-xs text-neutral-400">{t.seats} seats</span>
                            )}
                          </span>
                          {isHeld && (
                            <span className="shrink-0 text-[10px] font-bold bg-amber-700/50 text-amber-300 px-1.5 py-0.5 rounded">
                              HELD
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
                {tables.length === 0 && (
                  <p className="text-sm text-neutral-500 text-center py-8">No tables configured.</p>
                )}
              </div>

              {/* Footer */}
              <div className="shrink-0 flex gap-2 px-5 py-4 border-t border-neutral-700">
                {holdOpen.preAssigned && (
                  <button
                    onClick={() => handleHoldTable(holdOpen, holdOpen.preAssigned)}
                    className="flex-1 py-2 text-sm font-semibold rounded-xl bg-red-900/60 hover:bg-red-800/80 border border-red-700/50 text-red-300 transition-colors"
                  >
                    Clear Hold
                  </button>
                )}
                <button
                  onClick={() => setHoldOpen(null)}
                  className="flex-1 py-2 text-sm font-medium rounded-xl border border-neutral-600 text-neutral-300 hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {addOpen && (
        <AddGuestModal
          restaurantId={restaurantId}
          tableTypes={tableTypes}
          onClose={() => setAddOpen(false)}
        />
      )}
    </>
  );
}

// ─── TablesView ───────────────────────────────────────────────────────────────

export default function TablesView({ restaurantId, user }) {
  const [restaurantDoc,   setRestaurantDoc]   = useState(null);
  const [rawReservations, setRawReservations] = useState([]);
  const [itemsMeta,       setItemsMeta]       = useState([]);
  const [now,             setNow]             = useState(Date.now());
  const [loading,         setLoading]         = useState(true);
  const [selectedTable,   setSelectedTable]   = useState(null); // { table, reservation }
  const [editTablesMode,  setEditTablesMode]  = useState(false);
  const [editingTable,    setEditingTable]    = useState(null);  // table object being photo-edited

  // 10-second ticker
  useEffect(() => {
    const tid = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(tid);
  }, []);

  // Restaurant doc
  useEffect(() => {
    if (!restaurantId) return;
    const unsub = onSnapshot(
      doc(db, 'restaurants_data', restaurantId),
      snap => { setRestaurantDoc(snap.exists() ? snap.data() : null); setLoading(false); },
      () => setLoading(false),
    );
    return unsub;
  }, [restaurantId]);

  // Reservations — real-time
  useEffect(() => {
    if (!restaurantId) return;
    const unsub = onSnapshot(
      collection(db, 'restaurants_data', restaurantId, 'reservations'),
      snap => setRawReservations(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {},
    );
    return unsub;
  }, [restaurantId]);

  // Items — one-time for bubble categorization
  useEffect(() => {
    if (!restaurantId) return;
    getDocs(collection(db, 'restaurants_data', restaurantId, 'items'))
      .then(snap => setItemsMeta(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
  }, [restaurantId]);

  const tables = restaurantDoc?.tables || [];
  const barSeats = restaurantDoc?.barSeats || [];
  const reservationLength = restaurantDoc?.reservationLength || 60;

  const reservations = useMemo(
    () => processReservations(rawReservations, reservationLength, now),
    [rawReservations, reservationLength, now],
  );

  const resByTable = useMemo(() => {
    // Physical presence always wins over scheduling.
    // A paid/bussing party still on premises beats an upcoming next reservation.
    const priority = { seated: 0, unseated: 0, paid: 1, bussing: 2, upcoming: 3 };
    const map = {};
    reservations.filter(r => !r.isBarSeat).forEach(res => {
      const ids = Array.isArray(res.tableIds) ? res.tableIds : [String(res.tableId)];
      ids.forEach(tid => {
        const cur = map[tid];
        if (!cur || (priority[res.status] ?? 99) < (priority[cur.status] ?? 99)) {
          map[tid] = res;
        }
      });
    });
    return map;
  }, [reservations]);

  // Next upcoming reservation for tables already occupied — shown as a navy time pill
  const nextResByTable = useMemo(() => {
    const map = {};
    reservations.filter(r => !r.isBarSeat && r.status === 'upcoming').forEach(res => {
      const ids = Array.isArray(res.tableIds) ? res.tableIds : [String(res.tableId)];
      ids.forEach(tid => {
        const primary = resByTable[tid];
        if (!primary || primary.status === 'upcoming') return; // skip if table is free or itself upcoming
        const cur = map[tid];
        if (!cur || res.startMs < cur.startMs) map[tid] = res;
      });
    });
    return map;
  }, [reservations, resByTable]);

  const barResByTable = useMemo(() => {
    const PRIORITY = { seated: 0, paid: 1, bussing: 2 };
    const map = {};
    reservations.filter(r => r.isBarSeat).forEach(r => {
      const seatId = String(r.tableId);
      const cur = map[seatId];
      if (!cur || (PRIORITY[r.status] ?? 99) < (PRIORITY[cur.status] ?? 99)) {
        map[seatId] = r;
      }
    });
    return map;
  }, [reservations]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-7 h-7 border-2 border-[#5f799a] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!tables.length && !barSeats.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-neutral-400">
        <p className="text-sm">No tables or bar seats configured for this restaurant.</p>
      </div>
    );
  }

  const restaurantImage = restaurantDoc?.restaurantImage || null;

  return (
    <>
    <div className="flex flex-col gap-3 px-0 lg:px-4 pt-4 pb-6">

      {/* Stats — first thing visible */}
      <StatsBar tables={tables} resByTable={resByTable} tableBlocks={restaurantDoc?.tableBlocks || {}} now={now} />

      {/* Main layout: canvas left/top, right column (waitlist + upcoming) right/bottom */}
      <div className="flex flex-col lg:flex-row gap-3 items-start">

        {/* Canvas */}
        <div className="flex-1 min-w-0 w-full">
          <TableMapCanvas
            tables={tables}
            resByTable={resByTable}
            barSeats={barSeats}
            barResByTable={barResByTable}
            now={now}
            reservationLength={reservationLength}
            itemsMeta={itemsMeta}
            restaurantImage={restaurantImage}
            onSelect={setSelectedTable}
            tableBlocks={restaurantDoc?.tableBlocks || {}}
            editTablesMode={editTablesMode}
            onEditTable={(t) => { setEditingTable(t); }}
            canEditTables={user?.role === 'owner' || user?.permissions?.canEditTables === true}
            onToggleEditMode={() => setEditTablesMode(prev => !prev)}
            nextResByTable={nextResByTable}
          />
        </div>

        {/* Right column: Waitlist + Upcoming stacked */}
        <div className="flex flex-col gap-3 w-full lg:w-72 shrink-0">
          <WaitlistPanel
            restaurantId={restaurantId}
            tables={tables}
            reservations={reservations}
            rawReservations={rawReservations}
            reservationLength={reservationLength}
            tableBlocks={restaurantDoc?.tableBlocks || {}}
            now={now}
          />
          <UpcomingPanel
            rawReservations={rawReservations}
            reservationLength={reservationLength}
            now={now}
            restaurantDoc={restaurantDoc}
            restaurantId={restaurantId}
          />
          <PickupOrdersPanel restaurantId={restaurantId} />
          {restaurantDoc?.confirmationTexts?.enabled && (
            <ConfirmationsPanel
              rawReservations={rawReservations}
              restaurantId={restaurantId}
              now={now}
            />
          )}
        </div>

      </div>

      {/* Legend */}
      <Legend />

    </div>

    {editingTable && (
      <TablePhotoModal
        table={editingTable}
        restaurantId={restaurantId}
        restaurantDoc={restaurantDoc}
        onClose={() => setEditingTable(null)}
        onSaved={() => setEditingTable(null)}
      />
    )}

    {selectedTable && (
      <TableDetailModal
        table={selectedTable.table}
        reservation={selectedTable.reservation}
        reservationLength={reservationLength}
        now={now}
        itemsMeta={itemsMeta}
        restaurantId={restaurantId}
        restaurantDoc={restaurantDoc}
        user={user}
        rawReservations={rawReservations}
        onClose={() => setSelectedTable(null)}
      />
    )}
    </>
  );
}
